import React, { useEffect, useState, useImperativeHandle, forwardRef, useMemo, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup as LeafletPopup, Polyline, Polygon, Circle, CircleMarker, useMap, useMapEvents } from 'react-leaflet';
import type { PopupProps } from 'react-leaflet';
import L, { LatLngBounds } from 'leaflet';
import { validateLine, validatePolygon } from '../../utils/topology';
import { Topic, MapDrawing, Comment, BasemapId } from '../../types';
import LocationSearch from './LocationSearch';
import { UpvoteIcon, ChatIcon, LocateIcon, LayersIcon, DiskIcon, PointIcon, LineIcon, PolygonIcon } from '../common/Icons';
import LinkifiedText from '../common/LinkifiedText';
import { useNotifications } from '../Notifications/NotificationProvider';
import { getStandardZoomLevel } from './MapUtils';
import { buildEditorialHexCellGeometries } from '../../utils/editorialImport';
import './MapUtils'; // Import for side effects (icon configuration)
import { initHotspots } from '../../web/heatLayer';
import { BASEMAP_CONFIG, BASEMAP_OPTIONS, DEFAULT_BASEMAP } from '../../constants/basemaps';

const WORLD_BOUNDS: LatLngBounds = L.latLngBounds([-85, -180], [85, 180]);
const WORLD_BOUNDS_PADDED: LatLngBounds = WORLD_BOUNDS.pad(0.02);

const Popup: React.FC<PopupProps> = ({ autoPan = false, ...props }) => (
  <LeafletPopup autoPan={autoPan} {...props} />
);

type SearchPin = { lat: number; lng: number; label?: string; source?: 'search' | 'geolocate' };

interface MapComponentProps {
  topics: Topic[];
  comments: Comment[];
  drawings: MapDrawing[]; // Filtered drawings to display
  allDrawings?: MapDrawing[]; // All drawings (for checking which topics have drawings)
  selectedTopic?: Topic | null;
  onTopicSelect?: (topic: Topic) => void;
  // Select a comment by id (for clicks on drawings)
  onCommentSelectById?: (commentId: string) => void;
  onLocationSelect?: (lat: number, lng: number) => void;
  onDrawingCreate?: (drawing: any) => void;
  selectedLocation?: {lat: number; lng: number} | null;
  commentLocation?: {lat: number; lng: number} | null;
  showDrawingTools?: boolean;
  isCreating?: boolean;
  isCreatingComment?: boolean;
  userRole: 'READER' | 'AUTHOR' | 'MODERATOR';
  // Controlled drawing state (optional)
  drawingMode?: 'none' | 'marker' | 'line' | 'polygon';
  onDrawingModeChange?: (mode: 'none' | 'marker' | 'line' | 'polygon') => void;
  pathPoints?: { lat: number; lng: number }[];
  onPathPointsChange?: (points: { lat: number; lng: number }[]) => void;
  currentDrawing?: any | null;
  drawingControlsVisible?: boolean; // toggle built-in map UI panel
  // External focus controls from panel/list clicks
  focusPoint?: { lat: number; lng: number } | null;
  focusDrawing?: any | null; // MapDrawing-like object
  focusBounds?: { minLat: number; maxLat: number; minLng: number; maxLng: number } | null; // For multi-drawing focus
  // Panel state for centering adjustments
  isPanelExpanded?: boolean;
  // Selected comment for showing comment-specific drawings
  selectedComment?: Comment | null;
  // Counter to force re-focus when same comment is clicked
  commentClickCounter?: number;
  // Multi-select drawings mode
  multiSelectActive?: boolean;
  multiSelectedCommentIds?: string[];
  // Spatial search selection
  spatialSearchCenter?: { lat: number; lng: number } | null;
  spatialSearchRadiusKm?: number;
  spatialSearchPicking?: boolean;
  onSpatialSearchSelect?: (lat: number, lng: number) => void;
  // Called when map bounds change; bbox format: minLng,minLat,maxLng,maxLat
  onBoundsChange?: (bbox: string) => void;
  // Editing shape via panel
  editingActive?: boolean;
  onStartEditDrawing?: (drawing: any) => void;
  editingDrawingData?: any | null; // The actual drawing being edited
  // Current user for edit permissions
  currentUserId?: string;
  // Whether to show the floating on-map edit button
  showFloatingEditButton?: boolean;
  // Callback for clearing drawing state
  onClearDrawing?: () => void;
  // Open a user's public profile in the left panel
  onOpenUserProfile?: (userId: string) => void;
  // Trigger topic creation using a located marker
  onCreateTopicFromLocation?: (lat: number, lng: number) => void;
  activeBasemap?: BasemapId;
  onBasemapChange?: (id: BasemapId) => void;
  activeTopicsTab?: 'popular' | 'live' | 'private';
  // Disable auto-centering while App is resolving a comment-in-view
  suppressTopicFocus?: boolean;
  // Close popups when clicking on empty map area
  onClosePopups?: () => boolean;
}

  // Component to handle map events and fly to locations

  const MapController: React.FC<{
    selectedTopic?: Topic | null;
    searchLocation?: SearchPin | null;
    currentDrawing?: any | null;
    zoomToDrawing?: any | null;
    focusPoint?: { lat: number; lng: number } | null;
    focusDrawing?: any | null;
    focusBounds?: { minLat: number; maxLat: number; minLng: number; maxLng: number } | null;
    drawings?: MapDrawing[];
    isPanelExpanded?: boolean;
    selectedComment?: Comment | null;
    commentClickCounter?: number;
    comments?: Comment[];
    multiSelectActive?: boolean;
    multiSelectedCommentIds?: string[];
    suppressTopicFocus?: boolean;
    onBoundsChange?: (bbox: string) => void;
    ipRegion?: { lat: number; lng: number; zoom: number } | null;
  }> = ({ selectedTopic, searchLocation, currentDrawing, zoomToDrawing, focusPoint, focusDrawing, focusBounds, drawings = [], isPanelExpanded = false, selectedComment = null, commentClickCounter = 0, comments = [], multiSelectActive = false, multiSelectedCommentIds = [], suppressTopicFocus = false, onBoundsChange, ipRegion = null }) => {
    const map = useMap();

  // Ensure Leaflet recalculates size when layout changes (panel toggle, resize)
  useEffect(() => {
    const onResize = () => {
      try { map.invalidateSize(); } catch {}
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [map]);

    // When the side panel expands/collapses, wait for CSS transition then reflow map
    useEffect(() => {
      const id = window.setTimeout(() => {
        try { map.invalidateSize(); } catch {}
      }, 350); // matches CSS transition in panel
      return () => window.clearTimeout(id);
    }, [isPanelExpanded, map]);

    // Report bounds to parent on move/zoom end
    useEffect(() => {
      const report = () => {
        try {
          const b = map.getBounds();
          const bbox = `${b.getWest()},${b.getSouth()},${b.getEast()},${b.getNorth()}`;
          onBoundsChange && onBoundsChange(bbox);
        } catch {}
      };
      report();
      map.on('moveend', report);
      map.on('zoomend', report);
      return () => { try { map.off('moveend', report); map.off('zoomend', report); } catch {} };
    }, [map, onBoundsChange]);

    // Track recent user dragging to avoid auto-centering fighting the user
    const lastUserDragAtRef = React.useRef<number>(0);
    useEffect(() => {
      const onDragStart = () => { lastUserDragAtRef.current = Date.now(); };
      map.on('dragstart', onDragStart);
      return () => { try { map.off('dragstart', onDragStart); } catch {} };
    }, [map]);

    // On first load, if we have a rough IP region, center there at a country/region zoom.
    useEffect(() => {
      if (!ipRegion) return;
      console.log('[IP Geo] Applying to map:', ipRegion);
      try { map.setView([ipRegion.lat, ipRegion.lng], ipRegion.zoom, { animate: false }); } catch (e) { console.warn('[IP Geo] setView failed:', e); }
    }, [ipRegion, map]);

  useEffect(() => {
    if (!selectedTopic) return;
    if (suppressTopicFocus) return;
    // One-time suppression flag set by App when creating a comment without geometry
    try {
      if ((window as any).__suppressCommentFocusOnce) {
        (window as any).__suppressCommentFocusOnce = false;
        return; // skip any automatic focusing in this cycle
      }
    } catch {}
    const isMobile = window.innerWidth < 768;
    const pad = isMobile ? 20 : 50;
    const maxZoomCap = isMobile ? 16 : 17;
    const recentlyDragged = Date.now() - (lastUserDragAtRef.current || 0) < 1200;
    const lastFocusKeyRef = (MapController as any)._lastFocusKeyRef || ((MapController as any)._lastFocusKeyRef = React.createRef<string>());
    if (!lastFocusKeyRef.current) lastFocusKeyRef.current = '' as any;
    const focusKey = `${selectedTopic?.id || ''}|${selectedComment?.id || ''}|${multiSelectedCommentIds.join(',')}|${commentClickCounter}`;
    if (lastFocusKeyRef.current === focusKey) return;

    // If a specific comment is selected but it has no geometry (no drawing and no lat/lng),
    // do not auto-focus or fall back to the topic-level shape. Keep current map view.
    if (selectedComment) {
      const hasCommentDrawing = drawings.some(d => d.topicId === selectedTopic.id && d.commentId === selectedComment.id);
      const hasCommentPoint = typeof (selectedComment as any).latitude === 'number' && typeof (selectedComment as any).longitude === 'number';
      if (!hasCommentDrawing && !hasCommentPoint) {
        lastFocusKeyRef.current = focusKey; // mark handled to avoid repeated checks
        return;
      }
    }

    const fitBoundsSmart = (bounds: L.LatLngBoundsExpression, duration = 1.2) => {
      // Bias slightly away from the right edge to avoid visual clipping
      const rightExtra = isMobile ? 60 : 120;
      const leftExtra = isPanelExpanded ? (isMobile ? 10 : 20) : 0;
      map.fitBounds(bounds, {
        paddingTopLeft: [pad + leftExtra, pad],
        paddingBottomRight: [pad + rightExtra, pad],
        maxZoom: maxZoomCap,
        duration
      });
    };

    // Multi-select: fit to union of selected drawings
    if (!recentlyDragged && multiSelectActive && multiSelectedCommentIds.length > 0) {
      const chosen = drawings.filter(d => 
        d.topicId === selectedTopic.id && (
          (d.commentId ? multiSelectedCommentIds.includes(d.commentId) : multiSelectedCommentIds.includes('__TOPIC__'))
        )
      );
      if (chosen.length > 0) {
        const bounds = L.latLngBounds([] as any);
        let foundAny = false;
        chosen.forEach(d => {
          try {
            const g = JSON.parse(d.geometry);
            if (d.type === 'LINE' && g.type === 'LineString') {
              g.coordinates.forEach((c: number[]) => bounds.extend([c[1], c[0]]));
              foundAny = true;
            } else if (d.type === 'POLYGON' && g.type === 'Polygon') {
              g.coordinates[0].forEach((c: number[]) => bounds.extend([c[1], c[0]]));
              foundAny = true;
            } else if (d.type === 'POINT' && g.type === 'Point') {
              bounds.extend([g.coordinates[1], g.coordinates[0]]);
              foundAny = true;
            }
          } catch {}
        });
        if (foundAny && bounds.isValid()) {
          map.fitBounds(bounds, { padding: [pad, pad], maxZoom: maxZoomCap, duration: 1.2 });
          lastFocusKeyRef.current = focusKey;
          return;
        }
      }
    }

    // Editorial topics: fit to all topic-level drawings when no comment is selected
    if (!recentlyDragged && !selectedComment && selectedTopic.isEditorial) {
      const editorialDrawings = drawings.filter(d => d.topicId === selectedTopic.id && !d.commentId);
      if (editorialDrawings.length > 0) {
        const bounds = L.latLngBounds([] as any);
        let foundAny = false;
        editorialDrawings.forEach(d => {
          try {
            const g = JSON.parse(d.geometry);
            if (d.type === 'LINE' && g.type === 'LineString') {
              g.coordinates.forEach((c: number[]) => bounds.extend([c[1], c[0]]));
              foundAny = true;
            } else if (d.type === 'POLYGON' && g.type === 'Polygon') {
              g.coordinates[0].forEach((c: number[]) => bounds.extend([c[1], c[0]]));
              foundAny = true;
            } else if (d.type === 'POINT' && g.type === 'Point') {
              bounds.extend([g.coordinates[1], g.coordinates[0]]);
              foundAny = true;
            }
          } catch {}
        });
        if (foundAny && bounds.isValid()) {
          fitBoundsSmart(bounds, 1.2);
          lastFocusKeyRef.current = focusKey;
          return;
        }
      }
    }

    // Default: selected comment's drawing, else topic drawing
    const drawingToFocus = selectedComment
      ? drawings.find(d => d.topicId === selectedTopic.id && d.commentId === selectedComment.id)
      : drawings.find(d => d.topicId === selectedTopic.id && !d.commentId);

    if (!recentlyDragged && drawingToFocus) {
      try {
        const geometry = JSON.parse(drawingToFocus.geometry);
        if (drawingToFocus.type === 'LINE' && geometry.type === 'LineString') {
          const bounds = L.latLngBounds(geometry.coordinates.map((c: number[]) => [c[1], c[0]]));
          fitBoundsSmart(bounds, 1.2);
          lastFocusKeyRef.current = focusKey;
          return;
        }
        if (drawingToFocus.type === 'POLYGON' && geometry.type === 'Polygon') {
          const bounds = L.latLngBounds(geometry.coordinates[0].map((c: number[]) => [c[1], c[0]]));
          fitBoundsSmart(bounds, 1.2);
          lastFocusKeyRef.current = focusKey;
          return;
        }
        if (drawingToFocus.type === 'POINT' && geometry.type === 'Point') {
          map.flyTo([geometry.coordinates[1], geometry.coordinates[0]], getStandardZoomLevel(), { duration: 1.2 });
          lastFocusKeyRef.current = focusKey;
          return;
        }
      } catch {}
    }

    // If selected comment has coordinates, center there without rendering extra markers
    if (!recentlyDragged && selectedComment && selectedComment.latitude && selectedComment.longitude) {
      map.flyTo([selectedComment.latitude, selectedComment.longitude], getStandardZoomLevel(), { duration: 1.2 });
      lastFocusKeyRef.current = focusKey;
      return;
    }

    // Fallback: first comment with location, else topic location
    const firstLocated = comments.find(c => c.topicId === selectedTopic.id && c.latitude && c.longitude);
    if (!recentlyDragged && firstLocated) {
      map.flyTo([firstLocated.latitude as number, firstLocated.longitude as number], getStandardZoomLevel(), { duration: 1.2 });
      lastFocusKeyRef.current = focusKey;
      return;
    }
    if (!recentlyDragged && selectedTopic.latitude && selectedTopic.longitude) {
      map.flyTo([selectedTopic.latitude, selectedTopic.longitude], getStandardZoomLevel(), { duration: 1.2 });
      lastFocusKeyRef.current = focusKey;
    }
  }, [selectedTopic, drawings, selectedComment, commentClickCounter, isPanelExpanded, map, comments, multiSelectActive, multiSelectedCommentIds, suppressTopicFocus]);

  useEffect(() => {
    if (searchLocation) {
      // If a restore is in progress, avoid fighting it
      if ((window as any).__mapRestoreInProgress) return;
      // Always zoom to the user's current location when "Find Me" is used
      const zoomLevel = getStandardZoomLevel();
      map.flyTo([searchLocation.lat, searchLocation.lng], zoomLevel, { duration: 1.5 });
    }
  }, [searchLocation, map]);

  useEffect(() => {
  if ((window as any).__mapRestoreInProgress) return;
  if (currentDrawing) {
      const geometry = JSON.parse(currentDrawing.geometry);
      if (currentDrawing.type === 'LINE' && geometry.type === 'LineString') {
        const bounds = L.latLngBounds(geometry.coordinates.map((coord: number[]) => [coord[1], coord[0]]));
        map.fitBounds(bounds, { padding: [20, 20], maxZoom: 17, duration: 1.2 });
      } else if (currentDrawing.type === 'POLYGON' && geometry.type === 'Polygon') {
        const bounds = L.latLngBounds(geometry.coordinates[0].map((coord: number[]) => [coord[1], coord[0]]));
        map.fitBounds(bounds, { padding: [20, 20], maxZoom: 17, duration: 1.2 });
      } else if (currentDrawing.type === 'POINT' && geometry.type === 'Point') {
        map.flyTo([geometry.coordinates[1], geometry.coordinates[0]], getStandardZoomLevel(), { duration: 1.2 });
      }
    }
  }, [currentDrawing, map, isPanelExpanded]);

  // Handle zooming to existing drawings (when clicked on map)
  useEffect(() => {
  if ((window as any).__mapRestoreInProgress) return;
  if (!zoomToDrawing) return;
    const isMobile = window.innerWidth < 768;
    const geometry = JSON.parse(zoomToDrawing.geometry);
    if (zoomToDrawing.type === 'LINE' && geometry.type === 'LineString') {
      const bounds = L.latLngBounds(geometry.coordinates.map((coord: number[]) => [coord[1], coord[0]]));
      const rightExtra = isMobile ? 60 : 120;
      map.fitBounds(bounds, { paddingTopLeft: [50, 50], paddingBottomRight: [50 + rightExtra, 50], maxZoom: 17, duration: 1.2 });
    } else if (zoomToDrawing.type === 'POLYGON' && geometry.type === 'Polygon') {
      const bounds = L.latLngBounds(geometry.coordinates[0].map((coord: number[]) => [coord[1], coord[0]]));
      const rightExtra = isMobile ? 60 : 120;
      map.fitBounds(bounds, { paddingTopLeft: [50, 50], paddingBottomRight: [50 + rightExtra, 50], maxZoom: 17, duration: 1.2 });
    } else if (zoomToDrawing.type === 'POINT' && geometry.type === 'Point') {
      map.flyTo([geometry.coordinates[1], geometry.coordinates[0]], getStandardZoomLevel(), { duration: 1.2 });
    }
  }, [zoomToDrawing, map]);

  // Handle external focus requests from panel (point)
  useEffect(() => {
  if ((window as any).__mapRestoreInProgress) return;
  if (!focusPoint) return;
  map.flyTo([focusPoint.lat, focusPoint.lng], getStandardZoomLevel(), { duration: 1.2 });
  }, [focusPoint, map]);

  // Handle external focus requests from panel (drawing)
  useEffect(() => {
  if ((window as any).__mapRestoreInProgress) return;
  if (!focusDrawing) return;
    try {
      const geometry = JSON.parse(focusDrawing.geometry);
      const isMobile = window.innerWidth < 768;
      const pad = isMobile ? 20 : 50;
      const maxZoomCap = isMobile ? 16 : 17;
      // Bias slightly away from the right edge and nudge left when panel is expanded
      const rightExtra = isMobile ? 60 : 120;
      const leftExtra = isPanelExpanded ? (isMobile ? 10 : 20) : 0;

      if (focusDrawing.type === 'LINE' && geometry.type === 'LineString') {
        const coords = geometry.coordinates.map((c: number[]) => [c[1], c[0]]);
        const bounds = L.latLngBounds(coords as any);
        // Avoid over-zooming for very small shapes: use flyTo when extent is tiny
        try {
          const ne = bounds.getNorthEast();
          const sw = bounds.getSouthWest();
          const latSpan = Math.abs(ne.lat - sw.lat);
          const lngSpan = Math.abs(ne.lng - sw.lng);
          // Debug: helpful when tracing odd focus behavior
          
          if (latSpan < 0.0002 && lngSpan < 0.0002) {
            const c = bounds.getCenter();
            map.flyTo([c.lat, c.lng], getStandardZoomLevel(), { duration: 1.0 });
          } else {
            map.fitBounds(bounds, { paddingTopLeft: [pad + leftExtra, pad], paddingBottomRight: [pad + rightExtra, pad], maxZoom: maxZoomCap, duration: 1.2 });
          }
        } catch (e) {
          
          map.fitBounds(bounds, { paddingTopLeft: [pad + leftExtra, pad], paddingBottomRight: [pad + rightExtra, pad], maxZoom: maxZoomCap, duration: 1.2 });
        }
      } else if (focusDrawing.type === 'POLYGON' && geometry.type === 'Polygon') {
        const coords = geometry.coordinates[0].map((c: number[]) => [c[1], c[0]]);
        const bounds = L.latLngBounds(coords as any);
        try {
          const ne = bounds.getNorthEast();
          const sw = bounds.getSouthWest();
          const latSpan = Math.abs(ne.lat - sw.lat);
          const lngSpan = Math.abs(ne.lng - sw.lng);
          
          if (latSpan < 0.0002 && lngSpan < 0.0002) {
            const c = bounds.getCenter();
            map.flyTo([c.lat, c.lng], getStandardZoomLevel(), { duration: 1.0 });
          } else {
            map.fitBounds(bounds, { paddingTopLeft: [pad + leftExtra, pad], paddingBottomRight: [pad + rightExtra, pad], maxZoom: maxZoomCap, duration: 1.2 });
          }
        } catch (e) {
          
          map.fitBounds(bounds, { paddingTopLeft: [pad + leftExtra, pad], paddingBottomRight: [pad + rightExtra, pad], maxZoom: maxZoomCap, duration: 1.2 });
        }
      } else if (focusDrawing.type === 'POINT' && geometry.type === 'Point') {
        // Center points using an offset so they appear visible when the side panel is open
        const lat = geometry.coordinates[1];
        const lng = geometry.coordinates[0];
        // Use flyTo for points (keeps UX consistent with topic focus)
        map.flyTo([lat, lng], getStandardZoomLevel(), { duration: 1.2 });
      }
    } catch {}
  }, [focusDrawing, map, isPanelExpanded]);

  // Handle external focus requests for combined bounds (curated/editorial content)
  // Use a ref to track the last focused bounds and avoid re-triggering on panel changes
  const lastFocusBoundsRef = React.useRef<string | null>(null);
  useEffect(() => {
    if ((window as any).__mapRestoreInProgress) return;
    if (!focusBounds) return;
    const { minLat, maxLat, minLng, maxLng } = focusBounds;
    if (!Number.isFinite(minLat) || !Number.isFinite(maxLat) || !Number.isFinite(minLng) || !Number.isFinite(maxLng)) return;

    const boundsKey = `${minLat},${maxLat},${minLng},${maxLng}`;
    const isNewBounds = lastFocusBoundsRef.current !== boundsKey;
    lastFocusBoundsRef.current = boundsKey;

    // Delay fitBounds slightly to ensure panel layout has settled
    const timeoutId = window.setTimeout(() => {
      const isMobile = window.innerWidth < 768;
      const pad = isMobile ? 20 : 50;
      const maxZoomCap = isMobile ? 14 : 15; // Lower max zoom for large curated content
      // Keep a small inset from the panel edge without over-shifting the view
      const leftPadding = pad + (isPanelExpanded ? (isMobile ? 10 : 20) : 0);
      const rightPadding = pad;
      const topPadding = pad;
      const bottomPadding = pad;

      const bounds = L.latLngBounds([[minLat, minLng], [maxLat, maxLng]]);
      const latSpan = Math.abs(maxLat - minLat);
      const lngSpan = Math.abs(maxLng - minLng);

      if (latSpan < 0.0002 && lngSpan < 0.0002) {
        const c = bounds.getCenter();
        map.flyTo([c.lat, c.lng], getStandardZoomLevel(), { duration: 1.0 });
      } else {
        map.fitBounds(bounds, {
          paddingTopLeft: [leftPadding, topPadding],
          paddingBottomRight: [rightPadding, bottomPadding],
          maxZoom: maxZoomCap,
          duration: isNewBounds ? 1.2 : 0.5
        });
      }
    }, isNewBounds ? 150 : 50); // Longer delay for new bounds to let panel expand

    return () => window.clearTimeout(timeoutId);
  }, [focusBounds, map, isPanelExpanded]);

  return null;
};

export type MapComponentHandle = {
  setDrawingMode: (mode: 'none' | 'marker' | 'line' | 'polygon') => void;
  undoLastPoint: () => void;
  clearPath: () => void;
  finishShape: () => void;
  startEditDrawing: (drawing: any) => void;
  startEditCurrentDrawing: () => void;
  getDraftGeometry: (options?: { silent?: boolean }) => any | null;
  hasUnfinishedDrawing: () => boolean;
  getCurrentDrawingColor: () => string;
  getCurrentView: () => { center: [number, number]; zoom: number } | null;
  setView: (center: [number, number], zoom: number) => void;
  closeAllPopups: () => boolean;
  refreshHotspots: () => void;
};

function MapComponentInner(
  {
    topics,
    comments,
    drawings,
    allDrawings,
    selectedTopic,
    onTopicSelect,
    onCommentSelectById,
    onLocationSelect,
    onDrawingCreate,
    selectedLocation,
    commentLocation,
    showDrawingTools = false,
    isCreating = false,
    isCreatingComment = false,
    userRole,
    drawingControlsVisible = true,
    focusPoint,
    focusDrawing,
    focusBounds,
    drawingMode: externalDrawingMode,
    onDrawingModeChange,
    isPanelExpanded = false,
    selectedComment = null,
    commentClickCounter = 0,
    multiSelectActive = false,
    multiSelectedCommentIds = [],
    suppressTopicFocus = false,
    spatialSearchCenter = null,
    spatialSearchRadiusKm = 0,
    spatialSearchPicking = false,
    onSpatialSearchSelect,
    onBoundsChange,
    editingActive = false,
    onStartEditDrawing,
    editingDrawingData = null,
    currentUserId,
    showFloatingEditButton = false,
    onOpenUserProfile,
    onClearDrawing,
    onCreateTopicFromLocation,
    activeBasemap = DEFAULT_BASEMAP,
    onBasemapChange,
    activeTopicsTab = 'popular',
    onClosePopups,
  }: MapComponentProps & { onBoundsChange?: (bbox: string) => void },
  ref: React.Ref<MapComponentHandle>
) {
  const { notify } = useNotifications();
  // Default center: roughly Europe so first paint isn't too zoomed in
  const [mapCenter] = useState<[number, number]>([54.5260, 15.2551]);
  const [searchLocation, setSearchLocation] = useState<SearchPin | null>(null);
  const searchLocationSummary = useMemo(() => {
    if (!searchLocation?.label) return null;
    try {
      const parts = searchLocation.label.split(',').map(part => part.trim()).filter(Boolean);
      return parts.slice(0, 3).join(', ');
    } catch {
      return searchLocation.label;
    }
  }, [searchLocation?.label]);
  const [basemap, setBasemap] = useState<BasemapId>(activeBasemap);
  const [basemapExpanded, setBasemapExpanded] = useState<boolean>(false);
  const [drawingMode, setDrawingMode] = useState<'none' | 'marker' | 'line' | 'polygon'>('none');
  const [isDrawingFinished, setIsDrawingFinished] = useState(false);
  // Notify parent when map UI changes mode (avoid echo on programmatic changes)
  const applyDrawingMode = (mode: 'none' | 'marker' | 'line' | 'polygon') => {
    setDrawingMode(mode);
    setIsDrawingFinished(false); // Reset finished state when mode changes
    try { onDrawingModeChange && onDrawingModeChange(mode); } catch {}
  };
  const [pathPoints, setPathPoints] = useState<L.LatLngLiteral[]>([]);
  const [currentDrawing, setCurrentDrawing] = useState<any | null>(null);
  const [hideEditedShape, setHideEditedShape] = useState<boolean>(false);
  const [isLocating, setIsLocating] = useState(false);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [zoomToDrawing, setZoomToDrawing] = useState<any | null>(null);
  const [zoomLevel, setZoomLevel] = useState<number>(5);
  const [mapInstance, setMapInstance] = useState<any>(null);
  const hotspotsInitializedRef = useRef(false);
  const hotspotsInstanceRef = useRef<any>(null);
  const hexVisibilityTimerRef = useRef<number | null>(null);
  const [isHexLoading, setIsHexLoading] = useState(false);
  const autoPanCheckedRef = useRef(false);
  // Approximate region based on IP (no precise location prompt)
  const [ipRegion, setIpRegion] = useState<{ lat: number; lng: number; zoom: number } | null>(null);
  // Drawing color selection (persist last used)
  const defaultColor = '#2563eb';
  const [drawingColor, setDrawingColor] = useState<string>(() => {
    try { return localStorage.getItem('drawingColor') || defaultColor; } catch { return defaultColor; }
  });
  useEffect(() => {
    try { localStorage.setItem('drawingColor', drawingColor); } catch {}
  }, [drawingColor]);

  useEffect(() => {
    if (activeBasemap && activeBasemap !== basemap) {
      setBasemap(activeBasemap);
    }
  }, [activeBasemap, basemap]);

  // Control hexagon overlay visibility: hide while creating or viewing a topic (and on private tab)
  useEffect(() => {
    const instance = hotspotsInstanceRef.current;
    if (!instance || typeof instance.setEnabled !== 'function') return;
    const shouldShowHex = !isCreating && !selectedTopic && activeTopicsTab !== 'private';
    const applyVisibility = () => {
      try {
        instance.setEnabled(shouldShowHex);
        if (!shouldShowHex && typeof instance.clear === 'function') {
          instance.clear();
        }
      } catch (err) {
        console.error('[map] failed to toggle hex layer', err);
      }
    };
    const scheduleRetry = () => {
      if (hexVisibilityTimerRef.current) {
        window.clearTimeout(hexVisibilityTimerRef.current);
      }
      hexVisibilityTimerRef.current = window.setTimeout(() => {
        hexVisibilityTimerRef.current = null;
        if ((window as any).__mapRestoreInProgress) {
          scheduleRetry();
          return;
        }
        applyVisibility();
      }, 250);
    };
    if (shouldShowHex && (window as any).__mapRestoreInProgress) {
      scheduleRetry();
      return () => {
        if (hexVisibilityTimerRef.current) {
          window.clearTimeout(hexVisibilityTimerRef.current);
          hexVisibilityTimerRef.current = null;
        }
      };
    }
    if (hexVisibilityTimerRef.current) {
      window.clearTimeout(hexVisibilityTimerRef.current);
      hexVisibilityTimerRef.current = null;
    }
    applyVisibility();
    return () => {
      if (hexVisibilityTimerRef.current) {
        window.clearTimeout(hexVisibilityTimerRef.current);
        hexVisibilityTimerRef.current = null;
      }
    };
  }, [mapInstance, isCreating, selectedTopic, activeTopicsTab]);

  const handleSelectBasemap = (id: BasemapId) => {
    if (id === basemap) {
      setBasemapExpanded(false);
      return;
    }
    setBasemap(id);
    setBasemapExpanded(false);
    try { onBasemapChange && onBasemapChange(id); } catch {}
  };

  // Synchronize internal drawing mode with external prop
  useEffect(() => {
    if (externalDrawingMode !== undefined) {
      setDrawingMode(externalDrawingMode);
    }
  }, [externalDrawingMode]);

  // Estimate user's region from IP and center the map once
  useEffect(() => {
    const cacheKey = 'ipRegion.v1';
    const ttlMs = 24 * 60 * 60 * 1000; // 24h cache
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const obj = JSON.parse(cached);
        if (obj && obj.ts && (Date.now() - obj.ts) < ttlMs && typeof obj.lat === 'number' && typeof obj.lng === 'number') {
          console.log('[IP Geo] Using cached region:', obj);
          setIpRegion({ lat: obj.lat, lng: obj.lng, zoom: obj.zoom ?? 5 });
          return;
        } else {
          console.log('[IP Geo] Cache expired or invalid, fetching fresh');
        }
      }
    } catch {}

    const abort = new AbortController();
    const zoomForCountry = (cc: string): number => {
      const big = new Set(['US','RU','CA','AU','BR','CN','IN']);
      const small = new Set(['GB','IE','NL','BE','CH','AT','DK','PT','IL','SI','SG','HK','TW','KR','CZ','SK','LT','LV','EE','AL','MK','ME','XK','BA','MT','CY']);
      if (small.has(cc)) return 6;     // country-scale
      if (big.has(cc)) return 4;       // country is large → zoom out
      return 5;                        // default country scale
    };
    (async () => {
      try {
        // Use our backend proxy to avoid CSP issues
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        const res = await fetch(`${apiUrl}/api/geo/ip-location`, { signal: abort.signal });
        const data = await res.json();
        console.log('[IP Geo] Response:', data);
        const lat = parseFloat(String(data.lat ?? ''));
        const lng = parseFloat(String(data.lng ?? ''));
        const cc = String(data.countryCode || '').toUpperCase();
        if (!isNaN(lat) && !isNaN(lng)) {
          const ZOOM_BIAS = -1; // show a bit more area on first load
          const base = zoomForCountry(cc);
          const zoom = Math.max(2, Math.min(18, base + ZOOM_BIAS));
          console.log('[IP Geo] Setting region:', { lat, lng, zoom, cc });
          setIpRegion({ lat, lng, zoom });
          try { localStorage.setItem(cacheKey, JSON.stringify({ lat, lng, zoom, ts: Date.now() })); } catch {}
        } else {
          console.warn('[IP Geo] Invalid coordinates:', { lat, lng });
        }
      } catch (err) {
        console.warn('[IP Geo] Failed:', err);
      }
    })();
    return () => abort.abort();
  }, []);

  // State for editing existing shapes
  // const [editingDrawing, setEditingDrawing] = useState<any | null>(null); // Removed - using editingDrawingData prop directly
  const [, setEditingVertices] = useState<L.LatLngLiteral[]>([]);

  // Load existing shape vertices when editing starts
  useEffect(() => {
    if (editingActive && editingDrawingData) {
      try {
        const geometry = JSON.parse(editingDrawingData.geometry);
        let vertices: L.LatLngLiteral[] = [];
        
        if (editingDrawingData.type === 'LINE' && geometry.type === 'LineString') {
          vertices = geometry.coordinates.map((coord: number[]) => ({ lat: coord[1], lng: coord[0] }));
          setDrawingMode('line');
        } else if (editingDrawingData.type === 'POLYGON' && geometry.type === 'Polygon') {
          // Use the outer ring coordinates (first array in coordinates array)
          vertices = geometry.coordinates[0].map((coord: number[]) => ({ lat: coord[1], lng: coord[0] }));
          setDrawingMode('polygon');
        } else if (editingDrawingData.type === 'POINT' && geometry.type === 'Point') {
          vertices = [{ lat: geometry.coordinates[1], lng: geometry.coordinates[0] }];
          setDrawingMode('marker');
        }
        
        setEditingVertices(vertices);
        setPathPoints(vertices); // Use pathPoints for editing existing shapes
      } catch (error) {
        console.error('Failed to load editing vertices:', error);
      }
    } else if (!editingActive) {
      setEditingVertices([]);
    }
  }, [editingActive, editingDrawingData]);

  // Reset hidden flag when editing target changes or editing toggles
  useEffect(() => {
    if (editingActive) {
      setHideEditedShape(false);
    } else {
      setHideEditedShape(false);
    }
  }, [editingActive, editingDrawingData]);

  // Clean up drawing state when drawing tools are hidden (creation canceled)
  useEffect(() => {
    if (!showDrawingTools && !editingActive) {
      setDrawingMode('none');
      setPathPoints([]);
      setEditingVertices([]);
      // Don't clear currentDrawing for comment markers - let them persist
      if (!isCreatingComment || !currentDrawing || currentDrawing.type !== 'POINT') {
        setCurrentDrawing(null);
      }
    }
  }, [showDrawingTools, isCreatingComment, currentDrawing, editingActive]);

  // Only clear when switching to incompatible modes (marker or none)
  useEffect(() => {
    if (!editingActive) {
      if (drawingMode === 'marker' || drawingMode === 'none') {
        setPathPoints([]);
        // During creation mode, don't clear currentDrawing to preserve completed shapes
        if (!onDrawingCreate) {
          setCurrentDrawing(null);
        }
      }
    }
  }, [drawingMode, editingActive, onDrawingCreate]);

  // Expose imperative drawing controls to parent (panel)
  useImperativeHandle(ref, () => ({
    setDrawingMode: (mode) => {
      // Programmatic change from panel; do not re-notify parent
      setDrawingMode(mode);
      setIsDrawingFinished(false);
      if (mode === 'marker' || mode === 'none') {
        setPathPoints([]);
        setEditingVertices([]);
      }
    },
    undoLastPoint: () => {
      setPathPoints(prev => prev.slice(0, -1));
    },
    clearPath: () => {
      setPathPoints([]);
      setEditingVertices([]);
      // Also clear any temporary drawing (e.g., a marker placed during creation)
      setCurrentDrawing(null);
      setIsDrawingFinished(false);
      // If editing, also hide the underlying edited shape
      if (editingActive) {
        setHideEditedShape(true);
      }
    },
    finishShape: () => {
      if (editingActive) {
        // During editing, finishShape just validates the current geometry
        // The actual saving is handled by the parent component
        if (drawingMode === 'polygon' && pathPoints.length >= 3) {
          const val = validatePolygon(pathPoints);
          if (!val.ok) {
            notify({ type: 'warning', message: val.error || 'Invalid polygon' });
            return false;
          }
          return true;
        } else if (drawingMode === 'line' && pathPoints.length >= 2) {
          const val = validateLine(pathPoints);
          if (!val.ok) {
            notify({ type: 'warning', message: val.error || 'Invalid line' });
            return false;
          }
          return true;
        }
        return true; // For markers, always valid
      } else {
        // During creation mode, create new drawing
        if (drawingMode === 'polygon' && pathPoints.length >= 3) {
          const val = validatePolygon(pathPoints);
          if (!val.ok) {
            notify({ type: 'warning', message: val.error || 'Invalid polygon' });
            return;
          }
          const geoJson = {
            type: 'Polygon',
            coordinates: [pathPoints.map(p => [p.lng, p.lat])]
          } as any;
          const drawing = saveDrawingAsTemporary(geoJson, 'POLYGON');
          const c = pathPoints.reduce(
            (acc, p) => ({ lat: acc.lat + p.lat, lng: acc.lng + p.lng }),
            { lat: 0, lng: 0 }
          );
          const centroid = { lat: c.lat / pathPoints.length, lng: c.lng / pathPoints.length };
          onDrawingCreate && onDrawingCreate({ ...drawing, topicLocation: centroid });

          // Keep the drawing visible after finishing
          setCurrentDrawing(drawing);
          setDrawingMode('none');
          setPathPoints([]);
          setIsDrawingFinished(true);
        } else if (drawingMode === 'line' && pathPoints.length >= 2) {
          const val = validateLine(pathPoints);
          if (!val.ok) {
            notify({ type: 'warning', message: val.error || 'Invalid line' });
            return;
          }
          const geoJson = {
            type: 'LineString',
            coordinates: pathPoints.map(p => [p.lng, p.lat])
          } as any;
          const drawing = saveDrawingAsTemporary(geoJson, 'LINE');
          const c = pathPoints.reduce(
            (acc, p) => ({ lat: acc.lat + p.lat, lng: acc.lng + p.lng }),
            { lat: 0, lng: 0 }
          );
          const centroid = { lat: c.lat / pathPoints.length, lng: c.lng / pathPoints.length };
          onDrawingCreate && onDrawingCreate({ ...drawing, topicLocation: centroid });

          // Keep the drawing visible after finishing
          setCurrentDrawing(drawing);
          setDrawingMode('none');
          setPathPoints([]);
          setIsDrawingFinished(true);
        }
      }
    },
    startEditDrawing: (drawing: any) => {
      try {
        const geometry = JSON.parse(drawing.geometry);
        if (geometry.type === 'Point') {
          const coords = geometry.coordinates;
          setPathPoints([{ lat: coords[1], lng: coords[0] }]);
        } else if (geometry.type === 'LineString') {
          const coords = geometry.coordinates;
          setPathPoints(coords.map((coord: number[]) => ({ lat: coord[1], lng: coord[0] })));
        } else if (geometry.type === 'Polygon') {
          const coords = geometry.coordinates[0]; // Take outer ring
          setPathPoints(coords.map((coord: number[]) => ({ lat: coord[1], lng: coord[0] })));
        }
      } catch (e) {
        console.error('Failed to parse drawing geometry for editing:', e);
        setPathPoints([]);
      }
    },
    startEditCurrentDrawing: () => {
      if (currentDrawing) {
        try {
          const geometry = JSON.parse(currentDrawing.geometry);
          if (geometry.type === 'Point') {
            applyDrawingMode('marker');
            setPathPoints([{ lat: geometry.coordinates[1], lng: geometry.coordinates[0] }]);
          } else if (geometry.type === 'LineString') {
            applyDrawingMode('line');
            setPathPoints(geometry.coordinates.map((coord: number[]) => ({ lat: coord[1], lng: coord[0] })));
          } else if (geometry.type === 'Polygon') {
            applyDrawingMode('polygon');
            setPathPoints(geometry.coordinates[0].map((coord: number[]) => ({ lat: coord[1], lng: coord[0] })));
          }
          // Clear the current drawing so the path points show for editing
          setCurrentDrawing(null);
        } catch (error) {
          console.error('Error starting edit mode:', error);
          notify({ type: 'error', message: 'Could not start editing this drawing' });
        }
      }
    },
    getDraftGeometry: (options?: { silent?: boolean }) => {
      const silent = Boolean(options?.silent);
      try {
        if (editingActive) {
          // During editing, use pathPoints which contain the edited vertices
          if (drawingMode === 'marker' && pathPoints.length >= 1) {
            return { type: 'Point', coordinates: [pathPoints[0].lng, pathPoints[0].lat] };
          }
          if (drawingMode === 'line' && pathPoints.length >= 2) {
            const val = validateLine(pathPoints);
            if (!val.ok) { if (!silent) notify({ type: 'warning', message: val.error || 'Invalid line' }); return null; }
            return { type: 'LineString', coordinates: pathPoints.map(p => [p.lng, p.lat]) };
          }
          if (drawingMode === 'polygon' && pathPoints.length >= 3) {
            const val = validatePolygon(pathPoints);
            if (!val.ok) { if (!silent) notify({ type: 'warning', message: val.error || 'Invalid polygon' }); return null; }
            return { type: 'Polygon', coordinates: [pathPoints.map(p => [p.lng, p.lat])] };
          }
        } else {
          // During creation mode
          if (drawingMode === 'marker' && currentDrawing) {
            return JSON.parse(currentDrawing.geometry);
          }
          if (drawingMode === 'line' && pathPoints.length >= 2) {
            const val = validateLine(pathPoints);
            if (!val.ok) { if (!silent) notify({ type: 'warning', message: val.error || 'Invalid line' }); return null; }
            return { type: 'LineString', coordinates: pathPoints.map(p => [p.lng, p.lat]) };
          }
          if (drawingMode === 'polygon' && pathPoints.length >= 3) {
            const val = validatePolygon(pathPoints);
            if (!val.ok) { if (!silent) notify({ type: 'warning', message: val.error || 'Invalid polygon' }); return null; }
            return { type: 'Polygon', coordinates: [pathPoints.map(p => [p.lng, p.lat])] };
          }
        }
      } catch {}
      return null;
    },
    hasUnfinishedDrawing: () => {
      // Check if user is in drawing mode and has pathPoints but hasn't finished
      if (drawingMode === 'line' || drawingMode === 'polygon') {
        return pathPoints.length > 0; // Any pathPoints means drawing is in progress
      }
      return false;
    },
    getCurrentDrawingColor: () => {
      return drawingColor;
    },
    getCurrentView: () => {
      const view = window.__mapViewManager?.getCurrentView?.() || null;
      if (view) return view;
      if (mapInstance) {
        try {
          const center = mapInstance.getCenter();
          const zoom = mapInstance.getZoom();
          return { center: [center.lat, center.lng], zoom };
        } catch {}
      }
      return null;
    },
    setView: (center, zoom) => {
      if (window.__mapViewManager?.setView) {
        window.__mapViewManager.setView(center, zoom);
        return;
      }
      if (mapInstance?.setView) {
        mapInstance.setView(center, zoom, { animate: true });
      }
    },
    closeAllPopups: () => {
      let closedAny = false;
      try {
        if (mapInstance) {
          try {
            mapInstance.closePopup();
            closedAny = true;
          } catch {}
          try {
            mapInstance.stop?.();
          } catch {}
          try {
            mapInstance.eachLayer((layer: any) => {
              if (typeof layer.closePopup === 'function') {
                try {
                  layer.closePopup();
                  closedAny = true;
                } catch {}
              }
            });
          } catch {}
        }
        setZoomToDrawing(null);
      } catch (e) {
        // ignore close errors
      }
      return closedAny;
    },
    refreshHotspots: () => {
      try {
        hotspotsInstanceRef.current?.refresh?.();
      } catch (e) {
        console.error('[map] failed to refresh hex layer', e);
      }
    }
  }), [drawingMode, pathPoints, onDrawingCreate, editingActive, drawingColor, mapInstance]); // eslint-disable-line react-hooks/exhaustive-deps

  // Capture map instance for custom controls
  const MapInstanceCapture: React.FC = () => {
    const map = useMap();
    useEffect(() => {
      setMapInstance(map);
      try {
        map.setMaxBounds(WORLD_BOUNDS_PADDED);
        map.options.worldCopyJump = false;
        map.options.maxBoundsViscosity = 1.0;
        const minZoom = map.getBoundsZoom(WORLD_BOUNDS, true);
        if (Number.isFinite(minZoom)) {
          map.setMinZoom(minZoom);
          if (map.getZoom() < minZoom) {
            map.setZoom(minZoom);
          }
        }
        if (!hotspotsInitializedRef.current) {
          const envBase = (process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/$/, '');
          const apiBase = envBase.endsWith('/api') ? envBase.slice(0, -4) : envBase;
          try {
            hotspotsInstanceRef.current = initHotspots(map, { API_BASE: apiBase } as any);
            // Set up loading state callback
            if (hotspotsInstanceRef.current && hotspotsInstanceRef.current.onLoadingChange) {
              hotspotsInstanceRef.current.onLoadingChange((loading: boolean) => {
                setIsHexLoading(loading);
              });
            }
            // Auto-pan to content area if no hexes visible in user's region
            if (hotspotsInstanceRef.current && hotspotsInstanceRef.current.onHexCountChange) {
              hotspotsInstanceRef.current.onHexCountChange(async (count: number) => {
                if (autoPanCheckedRef.current) return;
                autoPanCheckedRef.current = true;
                if (count === 0) {
                  console.log('[IP Geo] No hexes visible, fetching content center');
                  try {
                    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
                    const res = await fetch(`${apiUrl}/api/geo/content-center`);
                    const data = await res.json();
                    if (data.lat && data.lng && data.topicCount > 0) {
                      console.log('[IP Geo] Panning to content center:', data);
                      map.setView([data.lat, data.lng], 5, { animate: true });
                    }
                  } catch (err) {
                    console.warn('[IP Geo] Failed to fetch content center:', err);
                  }
                }
              });
            }
          } catch (err) {
            console.error('Failed to initialize hotspots layer', err);
          }
          hotspotsInitializedRef.current = true;
        }
      } catch {}
      return () => {};
    }, [map]);
    return null;
  };

  // Watch map zoom level
  const ZoomWatcher: React.FC = () => {
    const map = useMap();
    useEffect(() => {
      setZoomLevel(map.getZoom());
    }, [map]);
    useMapEvents({
      zoomend: () => setZoomLevel(map.getZoom())
    });
    return null;
  };

  // MapViewManager component to handle map view state
  const MapViewManager: React.FC = () => {
    const map = useMap();
    
    useEffect(() => {
      // Initialize the global map view manager
      window.__mapViewManager = {
        getCurrentView: () => {
          try {
            const center = map.getCenter();
            const zoom = map.getZoom();
            return {
              center: [center.lat, center.lng] as [number, number],
              zoom
            };
          } catch (err) {
            
            return null;
          }
        },
        setView: (center: [number, number], zoom: number) => {
          try {
            map.setView(center, zoom, { animate: true });
          } catch (err) {
            
          }
        }
      };

      // Cleanup on unmount
      return () => {
        delete window.__mapViewManager;
      };
    }, [map]);

    return null;
  };

  const PopupPanHandler: React.FC = () => {
    const map = useMap();

    useEffect(() => {
      const baseOffsets = new WeakMap<any, L.Point>();

      const adjustPopup = (popup: any) => {
        if (!popup || typeof popup.getElement !== 'function') return;
        const popupEl = popup.getElement();
        if (!popupEl) return;
        const mapEl = map.getContainer();
        if (!mapEl) return;
        const mapRect = mapEl.getBoundingClientRect();
        const padding = 12;

        const currentBase = baseOffsets.get(popup) || (popup.options?.offset ? L.point(popup.options.offset) : L.point(0, 0));
        if (!baseOffsets.has(popup)) {
          baseOffsets.set(popup, currentBase);
        }

        // Reset to base offset before measuring
        try {
          popup.setOffset(currentBase);
          popup.update();
        } catch {}

        const popupRect = popupEl.getBoundingClientRect();
        let offsetX = 0;
        let offsetY = 0;

        if (popupRect.right + padding > mapRect.right) {
          offsetX -= popupRect.right - mapRect.right + padding;
        }
        if (popupRect.left - padding < mapRect.left) {
          offsetX += mapRect.left - popupRect.left + padding;
        }
        if (popupRect.bottom + padding > mapRect.bottom) {
          offsetY -= popupRect.bottom - mapRect.bottom + padding;
        }
        if (popupRect.top - padding < mapRect.top) {
          offsetY += mapRect.top - popupRect.top + padding;
        }

        if (offsetX || offsetY) {
          try {
            popup.setOffset(L.point(currentBase.x + offsetX, currentBase.y + offsetY));
            popup.update();
          } catch {}
        }
      };

      const onPopupOpen = (e: any) => {
        const popup = e?.popup;
        if (!popup) return;
        try {
          requestAnimationFrame(() => adjustPopup(popup));
          window.setTimeout(() => adjustPopup(popup), 280);
        } catch {}
      };

      map.on('popupopen', onPopupOpen);
      return () => { try { map.off('popupopen', onPopupOpen); } catch {} };
    }, [map]);

    return null;
  };

  // Simple client-side clustering without external deps
  const roundForZoom = (z: number) => {
    if (z <= 4) return 1; // ~100km
    if (z <= 6) return 2; // ~30km
    if (z <= 9) return 3; // ~5-10km
    if (z <= 12) return 4; // ~1-3km
    return 5; // close
  };

  type ClusterBucket = { key: string; lat: number; lng: number; count: number; topics: Topic[] };
  const buildBuckets = (ts: Topic[]): ClusterBucket[] => {
    // Collapsed view should always show topic markers regardless of drawings
    // to keep a consistent list/map experience. So do not filter by drawings here.
    const markerTopics = ts.filter(t => t.latitude != null && t.longitude != null);
    const dec = roundForZoom(zoomLevel);
    const factor = Math.pow(10, dec);
    const buckets = new Map<string, ClusterBucket>();
    for (const t of markerTopics) {
      const lat = t.latitude as number;
      const lng = t.longitude as number;
      const key = `${Math.round(lat * factor) / factor},${Math.round(lng * factor) / factor}`;
      const b = buckets.get(key) || { key, lat: 0, lng: 0, count: 0, topics: [] };
      b.lat += lat;
      b.lng += lng;
      b.count += 1;
      b.topics.push(t);
      buckets.set(key, b);
    }
    return Array.from(buckets.values()).map(b => ({ ...b, lat: b.lat / b.count, lng: b.lng / b.count }));
  };
  const topicsForActiveTab = useMemo(() => {
    if (selectedTopic) {
      return topics.filter(topic => topic.id === selectedTopic.id);
    }
    const isPrivateTopic = (topic: Topic) => topic.visibility !== 'PUBLIC';
    if (activeTopicsTab === 'private') {
      return topics.filter(isPrivateTopic);
    }
    return topics.filter(topic => !isPrivateTopic(topic));
  }, [topics, selectedTopic, activeTopicsTab]);

  const buckets = buildBuckets(topicsForActiveTab);

  // Handle map clicks: either add vertices (drawing) or set marker location
  const MapClickHandler: React.FC = () => {
    const map = useMap();
    useMapEvents({
      click: (e) => {
        // This should ALWAYS fire when map is clicked
        // map click handler
        if (!WORLD_BOUNDS.contains(e.latlng)) {
          try { map.panInsideBounds(WORLD_BOUNDS, { animate: true }); } catch {}
          return;
        }
        if (spatialSearchPicking && onSpatialSearchSelect) {
          onSpatialSearchSelect(e.latlng.lat, e.latlng.lng);
          return;
        }
        const isEditingActive = Boolean(editingActive);
        const isDrawingInteraction = (showDrawingTools || isEditingActive) && drawingMode !== 'none';
        if (searchLocation && isDrawingInteraction) {
          setSearchLocation(null);
        }
        
        if ((showDrawingTools || isEditingActive) && drawingMode === 'marker') {
          if (isEditingActive) {
            // editing existing point - move it to the new location
            setPathPoints([{ lat: e.latlng.lat, lng: e.latlng.lng }]);
            setEditingVertices([{ lat: e.latlng.lat, lng: e.latlng.lng }]);
          } else {
            // creating new marker drawing
            const drawing = saveDrawingAsTemporary({
              type: 'Point',
              coordinates: [e.latlng.lng, e.latlng.lat]
            }, 'POINT');
            
            // call onLocationSelect
            if (onLocationSelect) onLocationSelect(e.latlng.lat, e.latlng.lng);
            
            // call onDrawingCreate
            if (onDrawingCreate) onDrawingCreate(drawing);
          }
          // Don't clear drawing mode immediately - let parent handle it
          return;
        }
        if ((showDrawingTools || isEditingActive) && (drawingMode === 'line' || drawingMode === 'polygon')) {
          // adding point to path
          setPathPoints((prev) => {
            const newPoint = { lat: e.latlng.lat, lng: e.latlng.lng };
            
            // Check if this point is too close to any existing point (prevent duplicates)
            const minDistance = 0.0001; // roughly 10 meters
            const isDuplicate = prev.some(existingPoint => {
              const latDiff = Math.abs(existingPoint.lat - newPoint.lat);
              const lngDiff = Math.abs(existingPoint.lng - newPoint.lng);
              return latDiff < minDistance && lngDiff < minDistance;
            });
            
            // Don't add if it's too close to an existing point
            if (isDuplicate) {
              
              return prev;
            }
            
            const newPoints = [...prev, newPoint];
            
            return newPoints;
          });
          return;
        }
        if (onLocationSelect && userRole === 'AUTHOR' && drawingMode === 'none' && !showDrawingTools && !isEditingActive) {
          onLocationSelect(e.latlng.lat, e.latlng.lng);
          return;
        }
        // Click on empty map area - close any open popups
        // But skip if a hex popup was just opened (prevents immediate closing of hexagon popups)
        const hexPopupTime = (window as any).__hexPopupJustOpened;
        const hexPopupJustOpened = hexPopupTime && (Date.now() - hexPopupTime) < 100;
        if (onClosePopups && !showDrawingTools && !isEditingActive && !hexPopupJustOpened) {
          onClosePopups();
        }
      },
    });
    return null;
  };

  const handleLocationFound = (lat: number, lng: number, name: string) => {
    setSearchLocation({ lat, lng, label: name, source: 'search' });
    // location found
  };

  const handleFindMe = () => {
    if (!navigator.geolocation) {
      notify({ type: 'warning', message: 'Geolocation is not supported by this browser.' });
      return;
    }
    
    setIsLocating(true);
    // finding user location
    
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setSearchLocation({ lat: latitude, lng: longitude, label: 'Your current location', source: 'geolocate' });
        setIsLocating(false);
        // found user location
      },
      (err) => {
        setIsLocating(false);
        console.error('Geolocation error:', err.message);
        let errorMessage = 'Could not get your location. ';
        
        switch(err.code) {
          case err.PERMISSION_DENIED:
            errorMessage += 'Please allow location access and try again.';
            break;
          case err.POSITION_UNAVAILABLE:
            errorMessage += 'Location information is unavailable.';
            break;
          case err.TIMEOUT:
            errorMessage += 'Location request timed out.';
            break;
          default:
            errorMessage += 'An unknown error occurred.';
            break;
        }
        
        notify({ type: 'error', message: errorMessage });
      },
      { 
        enableHighAccuracy: true, 
        timeout: 10000, 
        maximumAge: 300000 // Cache location for 5 minutes
      }
    );
  };

  const saveDrawingAsTemporary = (geometry: any, type: 'POINT' | 'LINE' | 'POLYGON') => {
    const drawing = {
      id: `temp-${Date.now()}`,
      geometry: JSON.stringify(geometry),
      style: {
        color: drawingColor,
        fillColor: type === 'POLYGON' ? drawingColor : undefined,
        fillOpacity: type === 'POLYGON' ? 0.2 : 0,
        weight: 3,
        radius: type === 'POINT' ? 8 : undefined
      },
      type,
      temporary: true // Mark as temporary until topic is created
    };
    
    // Replace any existing drawing (only one drawing per topic)
    setCurrentDrawing(drawing);
    // saved temporary drawing
    
    return drawing;
  };

  // Saving of edits is handled by parent via getDraftGeometry()

  // Filter topics based on selection - show all when no topic selected, only selected topic when one is selected
  const visibleTopics = topicsForActiveTab;
  const suppressGeneralTopicMarkers = !selectedTopic && !isCreating && activeTopicsTab !== 'private';

  // Create topic markers
  const topicMarkers = visibleTopics.map(topic => {
    try {
      if (topic.latitude == null || topic.longitude == null) return null;

      // In the general map view, rely on the hex layer instead of mixing
      // plain topic pins on top of hotspot aggregation.
      if (suppressGeneralTopicMarkers) {
        return null;
      }

      // Hide all topic markers when a topic is selected (to focus on drawings)
      if (selectedTopic) {
        return null;
      }

      // Check if this topic has an associated drawing (use allDrawings for accurate check in general view)
      const drawingsToCheck = allDrawings || drawings;
      const topicDrawing = drawingsToCheck.find(d => d.topicId === topic.id && !d.commentId);
      const isTopicDrawingBeingEdited = editingActive && editingDrawingData && topicDrawing && topicDrawing.id === editingDrawingData.id;

      // Don't show topic marker if its drawing is being edited (to avoid double markers)
      if (isTopicDrawingBeingEdited) {
        return null;
      }

      // Don't show topic marker if it has any associated drawing (to avoid duplicates)
      if (topicDrawing) {
        return null;
      }

      const isSelected = false;

      return (
        <Marker 
          key={topic.id}
          position={[topic.latitude, topic.longitude]}
          eventHandlers={{
            click: () => onTopicSelect && onTopicSelect(topic)
          }}
        >
          <Popup autoPan={false} closeOnClick={false} closeButton={true} className="map-popup">
            <div style={{ maxWidth: '250px' }}>
              <h3 style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '4px' }}>
                {topic.title}
              </h3>
              <p style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                {(topic.content || '').substring(0, 100)}...
              </p>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#999', alignItems: 'center' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><UpvoteIcon size={12} /> {topic.upvotes}</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><ChatIcon size={12} /> {topic.commentCount}</span>
                <span>{new Date(topic.createdAt).toLocaleDateString()}</span>
              </div>
              {isSelected && (
                <div style={{ marginTop: '8px', padding: '4px 8px', backgroundColor: '#d1fae5', borderRadius: '4px', fontSize: '11px', color: '#065f46' }}>
                  📍 Selected Topic
                </div>
              )}
            </div>
          </Popup>
        </Marker>
      );
    } catch (err) {
      // Log topic-level render errors and continue rendering other markers
      try { console.error('Map: failed to render topic marker', { topicId: topic?.id, topic }, err); } catch (e) {}
      return null;
    }
  }).filter(Boolean as any);

  // Build a set of comments that already have a line/polygon/point drawing
  // Use allDrawings to get accurate list of all comments with drawings
  const drawingsForCheck = allDrawings || drawings;
  const commentsWithDrawings = new Set<string>(
    drawingsForCheck
      .filter(d => d.topicId === selectedTopic?.id && (d as any).commentId)
      .map(d => (d as any).commentId as string)
  );

  // Create comment markers (only for selected topic), but skip those that already have a drawing
  // Hide all comment markers when a specific comment is selected or multi-select is active
  const commentMarkers = (selectedTopic && !isCreatingComment && !selectedComment?.id && !multiSelectActive) ?
    comments
      .filter(comment => comment.topicId === selectedTopic.id && comment.latitude && comment.longitude && !commentsWithDrawings.has(comment.id))
      .map(comment => (
        <Marker 
          key={`comment-${comment.id}`}
          position={[comment.latitude!, comment.longitude!]}
        >
          <Popup autoPan={false} closeOnClick={false} closeButton={true} className="map-popup">
            <div style={{ maxWidth: '200px' }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#059669' }}>
                💬 Comment by{' '}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onOpenUserProfile && onOpenUserProfile(comment.user.id); }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#2563eb',
                    textDecoration: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    font: 'inherit'
                  }}
                  onMouseEnter={(e) => { (e.target as HTMLElement).style.textDecoration = 'underline'; }}
                  onMouseLeave={(e) => { (e.target as HTMLElement).style.textDecoration = 'none'; }}
                >
                  {comment.user.username}
                </button>
              </h4>
              <LinkifiedText
                as="p"
                text={comment.content}
                style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#999', alignItems: 'center' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><UpvoteIcon size={11} /> {comment.upvotes}</span>
                <span>{new Date(comment.createdAt).toLocaleDateString()}</span>
              </div>
            </div>
          </Popup>
        </Marker>
      )) : [];

  const renderDirectionsLink = (center: { lat: number; lng: number } | null) => {
    if (!center) return null;
    const destination = `${center.lat.toFixed(6)},${center.lng.toFixed(6)}`;
    const url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`;
    return (
      <div style={{ marginTop: 10 }}>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#2563eb', fontSize: 12, fontWeight: 600, textDecoration: 'none' }}
        >
          <LocateIcon size={14} /> Directions via Google Maps
        </a>
      </div>
    );
  };

  const getCenterFromPositions = (positions: Array<[number, number]>) => {
    const valid = positions.filter(([lat, lng]) => Number.isFinite(lat) && Number.isFinite(lng));
    if (!valid.length) return null;
    const bounds = L.latLngBounds(valid as any);
    return bounds.isValid() ? bounds.getCenter() : null;
  };

  const resolvePointStyle = (style: any) => ({
    color: style?.color ?? '#2563eb',
    weight: style?.weight ?? 2,
    opacity: style?.opacity ?? 1,
    fillColor: style?.fillColor ?? style?.color ?? '#2563eb',
    fillOpacity: style?.fillOpacity ?? 0.7
  });

  const resolvePointRadius = (style: any) => {
    const radius = style?.radius;
    return Number.isFinite(radius) ? Number(radius) : 6;
  };

  const renderDrawingAttributes = (properties?: Record<string, any>) => {
    if (!properties || typeof properties !== 'object') return null;
    const allEntries = Object.entries(properties)
      .filter(([key, value]) => key.trim().length > 0 && value !== null && value !== undefined && value !== '');
    if (!allEntries.length) return null;
    const entries = allEntries.slice(0, 6);
    const hasMore = allEntries.length > entries.length;
    const formatValue = (value: any) => {
      if (typeof value === 'string') return value;
      if (typeof value === 'number' || typeof value === 'boolean') return String(value);
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    };
    return (
      <div style={{ marginTop: 8, borderTop: '1px solid #e5e7eb', paddingTop: 6 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Attributes</div>
        <div style={{ display: 'grid', gap: 4 }}>
          {entries.map(([key, value]) => {
            const formatted = formatValue(value);
            const trimmed = formatted.length > 60 ? `${formatted.slice(0, 57)}…` : formatted;
            return (
              <div key={key} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 11 }}>
                <span style={{ color: '#374151', fontWeight: 600 }}>{key}</span>
                <span style={{ color: '#6b7280', textAlign: 'right' }}>{trimmed}</span>
              </div>
            );
          })}
        </div>
        {hasMore && (
          <div style={{ marginTop: 4, fontSize: 10, color: '#9ca3af' }}>
            {allEntries.length - entries.length} more…
          </div>
        )}
      </div>
    );
  };

  const buildPointSvg = (shape: string, size: number, style: any) => {
    const stroke = style?.color ?? '#2563eb';
    const strokeWidth = Math.max(0, Number(style?.weight ?? 2));
    const fill = style?.fillColor ?? stroke;
    const fillOpacity = Number.isFinite(style?.fillOpacity) ? Number(style.fillOpacity) : 0.7;
    const strokeOpacity = Number.isFinite(style?.opacity) ? Number(style.opacity) : 1;
    const inset = strokeWidth / 2;
    const half = size / 2;
    const safeSize = Math.max(4, size);
    const rectSize = Math.max(0, safeSize - strokeWidth);
    let body = '';
    if (shape === 'square') {
      body = `<rect x="${inset}" y="${inset}" width="${rectSize}" height="${rectSize}" rx="2" ry="2" fill="${fill}" fill-opacity="${fillOpacity}" stroke="${stroke}" stroke-width="${strokeWidth}" stroke-opacity="${strokeOpacity}" />`;
    } else if (shape === 'diamond') {
      body = `<polygon points="${half},${inset} ${safeSize - inset},${half} ${half},${safeSize - inset} ${inset},${half}" fill="${fill}" fill-opacity="${fillOpacity}" stroke="${stroke}" stroke-width="${strokeWidth}" stroke-opacity="${strokeOpacity}" />`;
    } else if (shape === 'triangle') {
      body = `<polygon points="${half},${inset} ${safeSize - inset},${safeSize - inset} ${inset},${safeSize - inset}" fill="${fill}" fill-opacity="${fillOpacity}" stroke="${stroke}" stroke-width="${strokeWidth}" stroke-opacity="${strokeOpacity}" />`;
    } else if (shape === 'hex') {
      const r = Math.max(2, (safeSize - strokeWidth) / 2);
      const cx = half;
      const cy = half;
      const points = Array.from({ length: 6 }, (_, i) => {
        const angle = (Math.PI / 3) * i - Math.PI / 6;
        const x = cx + r * Math.cos(angle);
        const y = cy + r * Math.sin(angle);
        return `${x},${y}`;
      }).join(' ');
      body = `<polygon points="${points}" fill="${fill}" fill-opacity="${fillOpacity}" stroke="${stroke}" stroke-width="${strokeWidth}" stroke-opacity="${strokeOpacity}" />`;
    } else if (shape === 'star') {
      const outer = Math.max(2, (safeSize - strokeWidth) / 2);
      const inner = outer * 0.5;
      const cx = half;
      const cy = half;
      const points = Array.from({ length: 10 }, (_, i) => {
        const angle = (Math.PI / 5) * i - Math.PI / 2;
        const r = i % 2 === 0 ? outer : inner;
        const x = cx + r * Math.cos(angle);
        const y = cy + r * Math.sin(angle);
        return `${x},${y}`;
      }).join(' ');
      body = `<polygon points="${points}" fill="${fill}" fill-opacity="${fillOpacity}" stroke="${stroke}" stroke-width="${strokeWidth}" stroke-opacity="${strokeOpacity}" />`;
    }
    return `<svg width="${safeSize}" height="${safeSize}" viewBox="0 0 ${safeSize} ${safeSize}" xmlns="http://www.w3.org/2000/svg">${body}</svg>`;
  };

  const buildPinSvg = (width: number, height: number, style: any) => {
    const stroke = style?.color ?? '#2563eb';
    const strokeWidth = Math.max(0, Number(style?.weight ?? 2));
    const fill = style?.fillColor ?? stroke;
    const fillOpacity = Number.isFinite(style?.fillOpacity) ? Number(style.fillOpacity) : 0.7;
    const strokeOpacity = Number.isFinite(style?.opacity) ? Number(style.opacity) : 1;
    const cx = width / 2;
    const top = strokeWidth / 2;
    const bottom = height - strokeWidth / 2;
    const left = strokeWidth / 2;
    const right = width - strokeWidth / 2;
    const bulb = height * 0.32;
    const path = [
      `M ${cx} ${bottom}`,
      `C ${right} ${height * 0.7} ${width * 0.98} ${height * 0.5} ${right} ${bulb}`,
      `C ${width * 0.92} ${height * 0.12} ${width * 0.75} ${top} ${cx} ${top}`,
      `C ${width * 0.25} ${top} ${width * 0.08} ${height * 0.12} ${left} ${bulb}`,
      `C ${width * 0.02} ${height * 0.5} ${left} ${height * 0.7} ${cx} ${bottom}`,
      'Z'
    ].join(' ');
    const eyeRadius = Math.max(2, width * 0.18);
    const eyeY = height * 0.38;
    return `<svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg">` +
      `<path d="${path}" fill="${fill}" fill-opacity="${fillOpacity}" stroke="${stroke}" stroke-width="${strokeWidth}" stroke-opacity="${strokeOpacity}" />` +
      `<circle cx="${cx}" cy="${eyeY}" r="${eyeRadius}" fill="#fff" fill-opacity="0.9" stroke="${stroke}" stroke-width="${Math.max(1, strokeWidth * 0.6)}" stroke-opacity="${strokeOpacity}" />` +
      `</svg>`;
  };

  const buildPointIcon = (shape: string, radius: number, style: any) => {
    if (shape === 'pin') {
      const base = Math.max(6, radius * 2);
      const width = Math.max(22, base * 1.6);
      const height = Math.max(36, base * 2.4);
      return L.divIcon({
        html: buildPinSvg(width, height, style),
        className: '',
        iconSize: [width, height],
        iconAnchor: [width / 2, height]
      });
    }
    const size = Math.max(4, radius * 2);
    const anchorY = size / 2;
    return L.divIcon({
      html: buildPointSvg(shape, size, style),
      className: '',
      iconSize: [size, size],
      iconAnchor: [size / 2, anchorY]
    });
  };

  const renderPointMarker = (params: {
    key: string;
    lat: number;
    lng: number;
    style: any;
    eventHandlers?: any;
    children?: React.ReactNode;
    allowCustomShape?: boolean;
  }) => {
    const allowCustomShape = Boolean(params.allowCustomShape);
    const shape = allowCustomShape ? (params.style?.shape || 'pin') : 'pin';
    const pointStyle = resolvePointStyle(params.style);
    const radius = resolvePointRadius(params.style);
    if (!allowCustomShape) {
      return (
        <Marker
          key={params.key}
          position={[params.lat, params.lng]}
          eventHandlers={params.eventHandlers}
        >
          {params.children}
        </Marker>
      );
    }
    if (shape === 'circle') {
      return (
        <CircleMarker
          key={params.key}
          center={[params.lat, params.lng]}
          radius={radius}
          pathOptions={pointStyle}
          eventHandlers={params.eventHandlers}
        >
          {params.children}
        </CircleMarker>
      );
    }
    const icon = buildPointIcon(shape, radius, pointStyle);
    return (
      <Marker
        key={params.key}
        position={[params.lat, params.lng]}
        icon={icon}
        eventHandlers={params.eventHandlers}
      >
        {params.children}
      </Marker>
    );
  };

  const drawingActive = showDrawingTools && (drawingMode === 'line' || drawingMode === 'polygon' || drawingMode === 'marker');

  const renderedDrawings = useMemo(() => {
    const multiActive = Array.isArray(multiSelectedCommentIds) && multiSelectedCommentIds.length > 0 && !!multiSelectActive;
    const available = Array.isArray(drawings) ? drawings : [];
    const isEditorialTopic = Boolean(selectedTopic?.isEditorial);
    const baseFiltered = available.filter((drawing) => {
      if (editingActive && editingDrawingData && drawing.id === editingDrawingData.id && hideEditedShape) {
        return false;
      }
      return true;
    });

    const applyMultiSelect = (candidates: typeof baseFiltered) => {
      if (!multiActive) return candidates;
      return candidates.filter((drawing) => {
        if (drawing.commentId) {
          return multiSelectedCommentIds.includes(drawing.commentId);
        }
        return multiSelectedCommentIds.includes('__TOPIC__');
      });
    };

    // Helper function to filter out POINT drawings when viewing lines/polygons
    const filterOutRedundantPoints = (candidateDrawings: typeof baseFiltered, keepAllPoints = false) => {
      // If keepAllPoints is true, don't filter out any POINT drawings
      if (keepAllPoints) {
        return candidateDrawings;
      }

      // Check if there are any LINE or POLYGON drawings in the set
      const hasLineOrPolygon = candidateDrawings.some(d => d.type === 'LINE' || d.type === 'POLYGON');

      if (!hasLineOrPolygon) {
        // No lines or polygons, so keep all POINTs (for standalone marker views)
        return candidateDrawings;
      }

      // When there are lines/polygons present, filter out ALL POINT drawings
      // This prevents showing vertex markers or other point markers alongside shapes
      return candidateDrawings.filter((drawing) => drawing.type !== 'POINT');
    };

    if (selectedTopic) {
      // While composing a new comment, hide persisted overlays for the topic so
      // the draft point/line/polygon can be drawn in the same extent.
      if (isCreatingComment && !editingActive) {
        return [];
      }

      const topicDrawings = baseFiltered.filter(drawing => drawing.topicId === selectedTopic.id);

      if (multiActive) {
        const selected = applyMultiSelect(topicDrawings);
        // When multi-selecting, keep all point markers visible alongside lines/polygons
        return filterOutRedundantPoints(selected, true);
      }

      if (selectedComment?.id) {
        // When a specific comment is selected, show that comment's drawings if it has any
        const commentDrawings = topicDrawings.filter(drawing => {
          // Must have a commentId
          if (!drawing.commentId) return false;
          // Must match the selected comment's ID (with type coercion to handle string/number mismatches)
          return String(drawing.commentId) === String(selectedComment.id);
        });
        // If the comment has drawings, show only those
        if (commentDrawings.length > 0) {
          return filterOutRedundantPoints(commentDrawings);
        }
        // If the comment has no drawings, fall through to show topic-level shapes
      }

      // For editorial topics OR topics with multiple topic-level drawings, show all
      const topicLevelDrawings = topicDrawings.filter(drawing => !drawing.commentId);
      if (isEditorialTopic || topicLevelDrawings.length > 1) {
        return filterOutRedundantPoints(topicLevelDrawings, true);
      }

      // When no specific comment is selected, show topic-level drawing or fallback
      if (topicLevelDrawings.length === 1) {
        return [topicLevelDrawings[0]];
      }

      // Fallback: only surface non-point comment drawings implicitly; hide point pins like the creator entry
      const nonPointCommentDrawings = topicDrawings
        .filter(drawing => drawing.commentId && drawing.type !== 'POINT');
      if (nonPointCommentDrawings.length > 0) {
        const sortedByCreated = [...nonPointCommentDrawings].sort((a, b) => {
          const aTime = Number.isFinite(Date.parse(a.createdAt as any)) ? Date.parse(a.createdAt as any) : 0;
          const bTime = Number.isFinite(Date.parse(b.createdAt as any)) ? Date.parse(b.createdAt as any) : 0;
          return aTime - bTime;
        });
        return filterOutRedundantPoints([sortedByCreated[0]]);
      }

      return [];
    }

    const finalDrawings = applyMultiSelect(baseFiltered);
    // When showing multiple drawings (no topic selected), keep all point markers visible
    return filterOutRedundantPoints(finalDrawings, true);
  }, [
    drawings,
    editingActive,
    editingDrawingData,
    hideEditedShape,
    isCreatingComment,
    multiSelectActive,
    multiSelectedCommentIds,
    selectedTopic,
    selectedComment?.id
  ]);

  const shouldRenderCurrentDrawing = Boolean(
    currentDrawing && !renderedDrawings.some((drawing) => drawing.id === currentDrawing.id)
  );

  return (
    <div style={{ height: '100%', width: '100%', position: 'relative' }} className={drawingActive ? 'drawing-active' : ''}>
      <MapContainer
        center={mapCenter}
        zoom={5}
        zoomControl={false}
        worldCopyJump={false}
        preferCanvas
        maxBounds={WORLD_BOUNDS_PADDED}
        maxBoundsViscosity={1.0}
        style={{ height: '100%', width: '100%', cursor: drawingActive ? 'crosshair' : undefined }}
      >
        {(() => {
          const activeConfig = BASEMAP_CONFIG[basemap] ?? BASEMAP_CONFIG[DEFAULT_BASEMAP];
          return (
            <>
              <TileLayer
                key={activeConfig.id}
                attribution={activeConfig.attribution}
                url={activeConfig.url}
                maxZoom={activeConfig.maxZoom}
                noWrap
              />
              {activeConfig.id === 'satellite' && (
                <TileLayer
                  key="satellite-labels"
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
                  maxZoom={19}
                  noWrap
                  pane="overlayPane"
                />
              )}
            </>
          );
        })()}
        
        <MapController selectedTopic={selectedTopic} searchLocation={searchLocation} currentDrawing={currentDrawing} zoomToDrawing={zoomToDrawing} focusPoint={focusPoint} focusDrawing={focusDrawing} focusBounds={focusBounds} drawings={drawings} isPanelExpanded={isPanelExpanded} selectedComment={selectedComment} commentClickCounter={commentClickCounter} comments={comments} multiSelectActive={multiSelectActive} multiSelectedCommentIds={multiSelectedCommentIds} suppressTopicFocus={suppressTopicFocus} onBoundsChange={onBoundsChange} ipRegion={ipRegion} />
        <MapInstanceCapture />
        <PopupPanHandler />
        <ZoomWatcher />
        <MapClickHandler />
        
        {/* Topic markers: plain markers in collapsed list; clusters in selected-topic view */}
        {!selectedTopic ? (
          <>{topicMarkers}</>
        ) : buckets.map(b => {
          if (b.count === 1) {
            const t = b.topics[0];
            
            // Hide the plain topic marker when a specific comment is selected.
            // This prevents the creator-entry pin from lingering while reviewing other comments.
            if (selectedTopic && selectedTopic.id === t.id && selectedComment) {
              return null;
            }
            
            // Check if this topic has an associated drawing
            const topicDrawing = drawings.find(d => d.topicId === t.id && !d.commentId);
            const isTopicDrawingBeingEdited = editingActive && editingDrawingData && topicDrawing && topicDrawing.id === editingDrawingData.id;
            
            // Don't show topic marker if its drawing is being edited (to avoid double markers)
            if (isTopicDrawingBeingEdited) {
              return null;
            }
            
            // Don't show topic marker if it has any associated drawing (to avoid duplicates)
            if (topicDrawing) {
              return null;
            }
            
            // Don't show topic marker if user is actively drawing (to prevent conflicts with new shapes)
            if (drawingActive && selectedTopic && selectedTopic.id === t.id) {
              return null;
            }
            
            return (
              <Marker 
                key={`t-${t.id}`}
                position={[t.latitude as number, t.longitude as number]}
                eventHandlers={{ click: () => onTopicSelect && onTopicSelect(t) }}
              >
                <Popup>
                  <div style={{ maxWidth: '250px' }}>
                    <h3 style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '4px' }}>{t.title}</h3>
                    <p style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>{t.content.substring(0, 100)}...</p>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#999', alignItems: 'center' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><UpvoteIcon size={12} /> {t.upvotes}</span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><ChatIcon size={12} /> {t.commentCount}</span>
                      <span>{new Date(t.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                </Popup>
              </Marker>
            );
          }
          // Cluster marker
          const icon = L.divIcon({
            html: `<div style="background:#2563eb;color:#fff;border-radius:16px;width:32px;height:32px;display:flex;align-items:center;justify-content:center;border:2px solid #fff;box-shadow:0 0 0 1px rgba(0,0,0,.2);font-weight:600;">${b.count}</div>`,
            className: '',
            iconSize: [32, 32],
            iconAnchor: [16, 16]
          });
          const ClusterMarker: React.FC = () => {
            const map = useMap();
            return (
              <Marker key={`c-${b.key}`} position={[b.lat, b.lng]} icon={icon} eventHandlers={{
                click: () => {
                  const next = Math.min(zoomLevel + 2, 18);
                  map.setView([b.lat, b.lng], next, { animate: true });
                }
              }}>
                <Popup>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontWeight: 600 }}>{b.count} topics here</div>
                    <div style={{ fontSize: 12, color: '#666' }}>Zoom in to view</div>
                  </div>
                </Popup>
              </Marker>
            );
          };
          return <ClusterMarker key={`cm-${b.key}`} />;
        })}
        {commentMarkers}

        {/* Live drawn shape preview */}
        {(showDrawingTools || editingActive) && drawingMode === 'line' && pathPoints.length > 0 && (
          <Polyline positions={pathPoints as any} pathOptions={{ color: drawingColor }} />
        )}
        {(showDrawingTools || editingActive) && drawingMode === 'polygon' && pathPoints.length > 1 && (
          <Polygon positions={pathPoints as any} pathOptions={{ color: drawingColor, fillColor: drawingColor, fillOpacity: 0.2 }} />
        )}

        {/* Live vertex points - draggable when editing, static when creating */}
        {(showDrawingTools || editingActive) && (drawingMode === 'line' || drawingMode === 'polygon') && pathPoints.map((p, idx) => {
          if (editingActive) {
            // Draggable vertex markers for editing mode
            const isFirstVertex = idx === 0;
            const isLastVertex = idx === pathPoints.length - 1;
            
            // Custom small marker icon for vertices
            const vertexIcon = L.divIcon({
              html: `<div style="background:${isFirstVertex ? '#10b981' : drawingColor};border:2px solid white;border-radius:50%;width:12px;height:12px;box-shadow:0 2px 4px rgba(0,0,0,0.3);cursor:move;"></div>`,
              className: '',
              iconSize: [12, 12],
              iconAnchor: [6, 6]
            });

            return (
              <Marker
                key={`vertex-${idx}-${p.lat}-${p.lng}`}
                position={[p.lat, p.lng]}
                icon={vertexIcon}
                draggable={true}
                eventHandlers={{
                  dragend: (e) => {
                    const marker = e.target;
                    const position = marker.getLatLng();
                    const newPoints = [...pathPoints];
                    newPoints[idx] = { lat: position.lat, lng: position.lng };
                    setPathPoints(newPoints);
                    setEditingVertices(newPoints);
                  }
                }}
              >
                <Popup>
                  <div style={{ textAlign: 'center', minWidth: '120px' }}>
                    <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 4 }}>
                      {isFirstVertex ? '🟢 Start Point' : isLastVertex ? '🔴 End Point' : `📍 Point ${idx + 1}`}
                    </div>
                    <div style={{ fontSize: 11, color: '#666' }}>
                      Drag to move vertex
                    </div>
                  </div>
                </Popup>
              </Marker>
            );
          } else {
            // Draggable vertex markers for creation mode
            const isFirstVertex = idx === 0;
            const isLastVertex = idx === pathPoints.length - 1;
            
            // Custom small marker icon for vertices during creation
            const vertexIcon = L.divIcon({
              html: `<div style="background:${isFirstVertex ? '#10b981' : drawingColor};border:2px solid white;border-radius:50%;width:${isLastVertex ? 12 : 8}px;height:${isLastVertex ? 12 : 8}px;box-shadow:0 2px 4px rgba(0,0,0,0.3);cursor:move;"></div>`,
              className: '',
              iconSize: [isLastVertex ? 12 : 8, isLastVertex ? 12 : 8],
              iconAnchor: [isLastVertex ? 6 : 4, isLastVertex ? 6 : 4]
            });
            
            return (
              <Marker
                key={`vertex-create-${idx}-${p.lat}-${p.lng}`}
                position={[p.lat, p.lng]}
                icon={vertexIcon}
                draggable={true}
                eventHandlers={{
                  dragend: (e) => {
                    const marker = e.target;
                    const position = marker.getLatLng();
                    const newPoints = [...pathPoints];
                    newPoints[idx] = { lat: position.lat, lng: position.lng };
                    setPathPoints(newPoints);
                  }
                }}
              >
                <Popup>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                      Vertex {idx + 1} {isFirstVertex && '(Start)'} {isLastVertex && '(End)'}
                    </div>
                    <div style={{ fontSize: 11, color: '#666' }}>
                      Drag to move vertex
                    </div>
                  </div>
                </Popup>
              </Marker>
            );
          }
        })}

        {/* Draggable marker for editing points/markers */}
        {editingActive && drawingMode === 'marker' && pathPoints.length > 0 && (
          <Marker
            position={[pathPoints[0].lat, pathPoints[0].lng]}
            draggable={true}
            eventHandlers={{
              dragend: (e) => {
                const marker = e.target;
                const position = marker.getLatLng();
                setPathPoints([{ lat: position.lat, lng: position.lng }]);
                setEditingVertices([{ lat: position.lat, lng: position.lng }]);
              }
            }}
          >
            <Popup>
              <div style={{ textAlign: 'center', minWidth: '120px' }}>
                <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 4 }}>
                  🔄 Editing Point
                </div>
                <div style={{ fontSize: 11, color: '#666' }}>
                  Drag to move or click map for new location
                </div>
              </div>
            </Popup>
          </Marker>
        )}
        
        {/* Render current temporary drawing */}
        {shouldRenderCurrentDrawing && (() => {
          const geometry = JSON.parse(currentDrawing.geometry);
          const style = currentDrawing.style;
          
          if (currentDrawing.type === 'POINT' && geometry.type === 'Point') {
            const allowCustomShape = Boolean(selectedTopic?.isEditorial && !selectedComment);
            return renderPointMarker({
              key: currentDrawing.id,
              lat: geometry.coordinates[1],
              lng: geometry.coordinates[0],
              style,
              allowCustomShape,
              children: <Popup>Drawing Marker</Popup>
            });
          } else if (currentDrawing.type === 'LINE' && geometry.type === 'LineString') {
            const positions = geometry.coordinates.map((coord: number[]) => [coord[1], coord[0]]);
            return (
              <Polyline 
                key={currentDrawing.id} 
                positions={positions} 
                pathOptions={style}
              >
                <Popup>Drawing Line</Popup>
              </Polyline>
            );
          } else if (currentDrawing.type === 'POLYGON' && geometry.type === 'Polygon') {
            const positions = geometry.coordinates[0].map((coord: number[]) => [coord[1], coord[0]]);
            return (
              <Polygon 
                key={currentDrawing.id} 
                positions={positions} 
                pathOptions={style}
              >
                <Popup>Drawing Polygon</Popup>
              </Polygon>
            );
          }
          return null;
        })()}
        
        {/* Render drawings from props (existing topic/comment drawings) */}
        {/* Sort to render POINTs last so they appear on top of polygons/lines */}
        {[...renderedDrawings].sort((a, b) => {
          const order = { 'POLYGON': 0, 'LINE': 1, 'POINT': 2 };
          return (order[a.type as keyof typeof order] || 0) - (order[b.type as keyof typeof order] || 0);
        }).map((drawing) => {
          const geometry = JSON.parse(drawing.geometry);
          const baseStyle = drawing.style || { color: '#666', weight: 2 };
          const isBeingEdited = Boolean(editingActive && editingDrawingData && editingDrawingData.id === drawing.id);
          const style = {
            ...baseStyle,
            opacity: isBeingEdited ? 0.7 : (baseStyle.opacity ?? 1),
            dashArray: isBeingEdited ? ((baseStyle as any).dashArray || '6,4') : (baseStyle as any).dashArray
          } as any;
          
          // Find associated topic and, if applicable, the specific comment for this drawing
          const associatedTopic = topics.find(t => t.id === drawing.topicId);
          const associatedComment = (drawing as any).commentId
            ? comments.find(c => c.id === (drawing as any).commentId)
            : null;
          const showTopicSummary = !selectedTopic || (associatedTopic && selectedTopic.id !== associatedTopic.id);
          
          if (drawing.type === 'LINE' && geometry.type === 'LineString') {
            const positions: Array<[number, number]> = geometry.coordinates.map((coord: number[]) => [coord[1], coord[0]]);
            const editedTag = (style as any)?.meta?.edited ? (
              <span style={{ color: '#6b7280', fontSize: 11, marginLeft: 6 }}>(edited)</span>
            ) : null;
            const directionsLink = renderDirectionsLink(getCenterFromPositions(positions));
            const attributesBlock = renderDrawingAttributes((drawing as any).properties);
            // More robust edit permission check
            const canEdit = Boolean(currentUserId && drawing.userId && String(currentUserId) === String(drawing.userId));
            return (
              <Polyline 
                key={drawing.id} 
                positions={positions} 
                pathOptions={style}
                eventHandlers={{
                  click: () => {
                    // If this is a comment drawing, select that comment in the panel
                    if ((drawing as any).commentId && onCommentSelectById) {
                      onCommentSelectById((drawing as any).commentId);
                    }
                    // Only switch topic if different; avoid clearing selected comment unnecessarily
                    if (associatedTopic && onTopicSelect && (!selectedTopic || selectedTopic.id !== associatedTopic.id)) {
                      onTopicSelect(associatedTopic);
                    }
                    // Skip auto-zoom for editorial/curated map drawings to prevent popup from closing
                    if (!associatedTopic?.isEditorial) {
                      setZoomToDrawing(drawing);
                      setTimeout(() => setZoomToDrawing(null), 100);
                    }
                  }
                }}
              >
                <Popup closeOnClick={false}>
                  <div style={{ maxWidth: '250px' }}>
                    {(associatedTopic || associatedComment) ? (
                      <>
                        {associatedComment ? (
                          <>
                            <h3 style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '4px' }}>
                              Comment by{' '}
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); onOpenUserProfile && onOpenUserProfile(associatedComment.user.id); }}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: '#2563eb',
                                  textDecoration: 'none',
                                  cursor: 'pointer',
                                  padding: 0,
                                  font: 'inherit'
                                }}
                                onMouseEnter={(e) => { (e.target as HTMLElement).style.textDecoration = 'underline'; }}
                                onMouseLeave={(e) => { (e.target as HTMLElement).style.textDecoration = 'none'; }}
                              >
                                {associatedComment.user.username}
                              </button>
                              {' '}{editedTag}
                            </h3>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#999', alignItems: 'center' }}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><UpvoteIcon size={12} /> {associatedComment.upvotes}</span>
                              <span>{new Date(associatedComment.createdAt).toLocaleDateString()}</span>
                            </div>
                          </>
                        ) : (
                          associatedTopic && (
                            <>
                              {showTopicSummary && (
                                <>
                                  <h3 style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '4px' }}>
                                    {associatedTopic.title} {editedTag}
                                  </h3>
                                  <LinkifiedText
                                    as="p"
                                    text={associatedTopic.content}
                                    style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}
                                  />
                                </>
                              )}
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#999', alignItems: 'center' }}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><UpvoteIcon size={12} /> {associatedTopic.upvotes}</span>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><ChatIcon size={12} /> {associatedTopic.commentCount}</span>
                                <span>{new Date(associatedTopic.createdAt).toLocaleDateString()}</span>
                              </div>
                            </>
                          )
                        )}
                      </>
                    ) : (
                      // Show basic drawing info when no associated content
                      <div style={{ textAlign: 'center', padding: '8px 0' }}>
                        <h3 style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '4px', color: '#374151' }}>
                          📏 Drawing Line {editedTag}
                        </h3>
                        <p style={{ fontSize: '12px', color: '#666' }}>
                          Stand-alone line drawing
                        </p>
                      </div>
                    )}
                    {attributesBlock}
                    {directionsLink}
                    
                    {/* Edit button disabled per request */}
                    {false && currentUserId && !editingActive && (
                      <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                        <button
                          onClick={(e) => { e.stopPropagation(); onStartEditDrawing && onStartEditDrawing(drawing); }}
                          style={{ 
                            fontSize: 12, 
                            padding: '6px 12px', 
                            border: '1px solid #3b82f6', 
                            borderRadius: 6, 
                            background: canEdit ? '#3b82f6' : '#f3f4f6',
                            color: canEdit ? '#fff' : '#374151',
                            cursor: 'pointer',
                            fontWeight: 500
                          }}
                          title={canEdit ? 'Edit your shape' : 'Edit shape (test mode)'}
                        >
                          ✏️ Edit Line
                        </button>
                      </div>
                    )}
                  </div>
                </Popup>
              </Polyline>
            );
          } else if (drawing.type === 'POLYGON' && geometry.type === 'Polygon') {
            const polygonGeometries = associatedTopic?.isEditorial && !(style as any)?.meta?.editorialHexified
              ? buildEditorialHexCellGeometries(geometry)
              : [geometry];
            const polygonPositionSets: Array<Array<[number, number]>> = polygonGeometries
              .filter((geom) => geom?.type === 'Polygon' && Array.isArray(geom?.coordinates?.[0]))
              .map((geom) => geom.coordinates[0].map((coord: number[]) => [coord[1], coord[0]]));
            if (!polygonPositionSets.length) return null;
            const editedTag = (style as any)?.meta?.edited ? (
              <span style={{ color: '#6b7280', fontSize: 11, marginLeft: 6 }}>(edited)</span>
            ) : null;
            const directionsLink = renderDirectionsLink(getCenterFromPositions(polygonPositionSets.flat()));
            const attributesBlock = renderDrawingAttributes((drawing as any).properties);
            // More robust edit permission check
            const canEdit = Boolean(currentUserId && drawing.userId && String(currentUserId) === String(drawing.userId));
            return (
              <>
                {polygonPositionSets.map((positions, polygonIndex) => (
                  <Polygon
                    key={`${drawing.id}-${polygonIndex}`}
                    positions={positions}
                    pathOptions={style}
                    eventHandlers={{
                      click: () => {
                        if ((drawing as any).commentId && onCommentSelectById) {
                          onCommentSelectById((drawing as any).commentId);
                        }
                        if (associatedTopic && onTopicSelect && (!selectedTopic || selectedTopic.id !== associatedTopic.id)) {
                          onTopicSelect(associatedTopic);
                        }
                        // Skip auto-zoom for editorial/curated map drawings to prevent popup from closing
                        if (!associatedTopic?.isEditorial) {
                          setZoomToDrawing(drawing);
                          setTimeout(() => setZoomToDrawing(null), 100);
                        }
                      }
                    }}
                  >
                    <Popup closeOnClick={false}>
                      <div style={{ maxWidth: '250px' }}>
                        {(associatedTopic || associatedComment) ? (
                          <>
                            {associatedComment ? (
                              <>
                                <h3 style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '4px' }}>
                                  Comment by{' '}
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); onOpenUserProfile && onOpenUserProfile(associatedComment.user.id); }}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      color: '#2563eb',
                                      textDecoration: 'none',
                                      cursor: 'pointer',
                                      padding: 0,
                                      font: 'inherit'
                                    }}
                                    onMouseEnter={(e) => { (e.target as HTMLElement).style.textDecoration = 'underline'; }}
                                    onMouseLeave={(e) => { (e.target as HTMLElement).style.textDecoration = 'none'; }}
                                  >
                                    {associatedComment.user.username}
                                  </button>
                                  {' '}{editedTag}
                                </h3>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#999', alignItems: 'center' }}>
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><UpvoteIcon size={12} /> {associatedComment.upvotes}</span>
                                  <span>{new Date(associatedComment.createdAt).toLocaleDateString()}</span>
                                </div>
                              </>
                            ) : (
                              associatedTopic && (
                                <>
                                  {showTopicSummary && (
                                    <>
                                      <h3 style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '4px' }}>
                                        {associatedTopic.title} {editedTag}
                                      </h3>
                                      <LinkifiedText
                                        as="p"
                                        text={associatedTopic.content}
                                        style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}
                                      />
                                    </>
                                  )}
                                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#999', alignItems: 'center' }}>
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><UpvoteIcon size={12} /> {associatedTopic.upvotes}</span>
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><ChatIcon size={12} /> {associatedTopic.commentCount}</span>
                                    <span>{new Date(associatedTopic.createdAt).toLocaleDateString()}</span>
                                  </div>
                                </>
                              )
                            )}
                          </>
                        ) : (
                          <div style={{ textAlign: 'center', padding: '8px 0' }}>
                            <h3 style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '4px', color: '#374151' }}>
                              🔺 Drawing Area {editedTag}
                            </h3>
                            <p style={{ fontSize: '12px', color: '#666' }}>
                              Stand-alone area drawing
                            </p>
                          </div>
                        )}
                        {attributesBlock}
                        {directionsLink}

                        {/* Edit button disabled per request */}
                        {false && currentUserId && !editingActive && (
                          <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                            <button
                              onClick={(e) => { e.stopPropagation(); onStartEditDrawing && onStartEditDrawing(drawing); }}
                              style={{
                                fontSize: 12,
                                padding: '6px 12px',
                                border: '1px solid #3b82f6',
                                borderRadius: 6,
                                background: canEdit ? '#3b82f6' : '#f3f4f6',
                                color: canEdit ? '#fff' : '#374151',
                                cursor: 'pointer',
                                fontWeight: 500
                              }}
                              title={canEdit ? 'Edit your shape' : 'Edit shape (test mode)'}
                            >
                              ✏️ Edit Area
                            </button>
                          </div>
                        )}
                      </div>
                    </Popup>
                  </Polygon>
                ))}
              </>
            );
          } else if (drawing.type === 'POINT' && geometry.type === 'Point') {
            const editedTag = (style as any)?.meta?.edited ? (
              <span style={{ color: '#6b7280', fontSize: 11, marginLeft: 6 }}>(edited)</span>
            ) : null;
            const pointLat = Number((geometry as any)?.coordinates?.[1]);
            const pointLng = Number((geometry as any)?.coordinates?.[0]);
            const hasValidPoint = Number.isFinite(pointLat) && Number.isFinite(pointLng);
            const directionsLink = renderDirectionsLink(hasValidPoint ? { lat: pointLat, lng: pointLng } : null);
            const attributesBlock = renderDrawingAttributes((drawing as any).properties);
            // More robust edit permission check
            const canEdit = Boolean(currentUserId && drawing.userId && String(currentUserId) === String(drawing.userId));
            const allowCustomShape = Boolean(
              (associatedTopic?.isEditorial && !drawing.commentId) ||
              (drawing as any).userId === '__EDITORIAL_PREVIEW__' ||
              (drawing as any).topicId === '__EDITORIAL_PREVIEW__'
            );
            return renderPointMarker({
              key: drawing.id,
              lat: geometry.coordinates[1],
              lng: geometry.coordinates[0],
              style,
              allowCustomShape,
              eventHandlers: {
                click: () => {
                  if ((drawing as any).commentId && onCommentSelectById) {
                    onCommentSelectById((drawing as any).commentId);
                  }
                  if (associatedTopic && onTopicSelect && (!selectedTopic || selectedTopic.id !== associatedTopic.id)) {
                    onTopicSelect(associatedTopic);
                  }
                  // Skip auto-zoom for editorial/curated map drawings to prevent popup from closing
                  if (!allowCustomShape) {
                    setZoomToDrawing(drawing);
                    setTimeout(() => setZoomToDrawing(null), 100);
                  }
                }
              },
              children: (
                <Popup closeOnClick={false}>
                  <div style={{ maxWidth: '250px' }}>
                    {(associatedTopic || associatedComment) ? (
                      <>
                        {associatedComment ? (
                          <>
                            <h3 style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '4px' }}>
                              Comment by{' '}
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); onOpenUserProfile && onOpenUserProfile(associatedComment.user.id); }}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: '#2563eb',
                                  textDecoration: 'none',
                                  cursor: 'pointer',
                                  padding: 0,
                                  font: 'inherit'
                                }}
                                onMouseEnter={(e) => { (e.target as HTMLElement).style.textDecoration = 'underline'; }}
                                onMouseLeave={(e) => { (e.target as HTMLElement).style.textDecoration = 'none'; }}
                              >
                                {associatedComment.user.username}
                              </button>
                              {' '}{editedTag}
                            </h3>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#999', alignItems: 'center' }}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><UpvoteIcon size={12} /> {associatedComment.upvotes}</span>
                              <span>{new Date(associatedComment.createdAt).toLocaleDateString()}</span>
                            </div>
                          </>
                        ) : (
                          associatedTopic && (
                            <>
                              {showTopicSummary && (
                                <>
                                  <h3 style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '4px' }}>
                                    {associatedTopic.title} {editedTag}
                                  </h3>
                                  <LinkifiedText
                                    as="p"
                                    text={associatedTopic.content}
                                    style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}
                                  />
                                </>
                              )}
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#999', alignItems: 'center' }}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><UpvoteIcon size={12} /> {associatedTopic.upvotes}</span>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><ChatIcon size={12} /> {associatedTopic.commentCount}</span>
                                <span>{new Date(associatedTopic.createdAt).toLocaleDateString()}</span>
                              </div>
                            </>
                          )
                        )}
                      </>
                    ) : (
                      // Show basic drawing info when no associated content
                      <div style={{ textAlign: 'center', padding: '8px 0' }}>
                        <h3 style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '4px', color: '#374151' }}>
                          📍 Drawing Point {editedTag}
                        </h3>
                        <p style={{ fontSize: '12px', color: '#666' }}>
                          Stand-alone point drawing
                        </p>
                      </div>
                    )}
                    {attributesBlock}
                    {directionsLink}
                    
                    {/* Don't show edit button for point drawings */}
                    {false && currentUserId && (
                      <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                        <button
                          onClick={(e) => { e.stopPropagation(); onStartEditDrawing && onStartEditDrawing(drawing); }}
                          style={{ 
                            fontSize: 12, 
                            padding: '6px 12px', 
                            border: '1px solid #3b82f6', 
                            borderRadius: 6, 
                            background: canEdit ? '#3b82f6' : '#f3f4f6',
                            color: canEdit ? '#fff' : '#374151',
                            cursor: 'pointer',
                            fontWeight: 500
                          }}
                          title={canEdit ? 'Edit your shape' : 'Edit shape (test mode)'}
                        >
                          ✏️ Edit Point
                        </button>
                      </div>
                    )}
                  </div>
                </Popup>
              )
            });
          }
          return null;
        })}
        
        {/* Selected location marker for topic creation - only show for point drawings or when no drawing exists */}
        {selectedLocation && (!currentDrawing || currentDrawing.type === 'POINT') && (
          <Marker position={[selectedLocation.lat, selectedLocation.lng]}>
            <Popup>
              <div style={{ textAlign: 'center' }}>
                <h4 style={{ margin: '0 0 8px 0', color: '#059669' }}>📍 New Topic Location</h4>
                <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
                  {selectedLocation.lat.toFixed(4)}, {selectedLocation.lng.toFixed(4)}
                </p>
              </div>
            </Popup>
          </Marker>
        )}

        {spatialSearchCenter && (spatialSearchRadiusKm ?? 0) > 0 && (
          <Circle
            center={[spatialSearchCenter.lat, spatialSearchCenter.lng]}
            radius={(spatialSearchRadiusKm || 0) * 1000}
            pathOptions={{
              color: '#2563eb',
              weight: 3,
              opacity: 0.95,
              fillColor: '#2563eb',
              fillOpacity: 0.14,
              dashArray: '4 6'
            }}
          >
            <Popup>
              <div style={{ minWidth: 180 }}>
                <h4 style={{ margin: '0 0 6px', color: '#047857', fontSize: 14 }}>Search area</h4>
                <p style={{ margin: '0 0 4px', fontSize: 12, color: '#374151' }}>
                  Center: {spatialSearchCenter.lat.toFixed(4)}, {spatialSearchCenter.lng.toFixed(4)}
                </p>
                <p style={{ margin: 0, fontSize: 12, color: '#374151' }}>
                  Radius: {spatialSearchRadiusKm?.toFixed(1)} km
                </p>
              </div>
            </Popup>
          </Circle>
        )}

        {/* User location marker (Find Me) */}
        {searchLocation && (
          <Marker
            key={`search-${searchLocation.lat}-${searchLocation.lng}`}
            position={[searchLocation.lat, searchLocation.lng]}
            eventHandlers={{ add: (evt) => { try { evt.target.openPopup(); } catch {} } }}
          >
            <Popup>
              <div style={{ textAlign: 'center', maxWidth: 240 }}>
                <h4 style={{ margin: '0 0 6px 0', color: '#2563eb', fontSize: '14px' }}>
                  📍 {searchLocation.source === 'geolocate' ? 'Your Location' : 'Found Location'}
                </h4>
                {searchLocationSummary && (
                  <p
                    style={{ margin: '0 0 6px 0', fontSize: '12px', color: '#374151' }}
                    title={searchLocation.label || undefined}
                  >
                    {searchLocationSummary}
                  </p>
                )}
                <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
                  {searchLocation.lat.toFixed(4)}, {searchLocation.lng.toFixed(4)}
                </p>
                {currentUserId && onCreateTopicFromLocation ? (
                  <>
                    <p style={{ margin: '10px 0 12px', fontSize: '12px', color: '#4b5563' }}>
                      This pin will be saved as the map shape for your new topic. You can refine details after opening the form.
                    </p>
                    <button
                      className="btn btn-primary"
                      style={{ fontSize: '13px', padding: '8px 14px', display: 'inline-flex', alignItems: 'center', gap: 8 }}
                      onClick={(e) => {
                        e.preventDefault();
                        onCreateTopicFromLocation(searchLocation.lat, searchLocation.lng);
                        setSearchLocation(null);
                      }}
                    >
                      <PointIcon size={14} /> Create topic with this pin
                    </button>
                  </>
                ) : (
                  <p style={{ margin: '10px 0 0', fontSize: '12px', color: '#6b7280' }}>
                    Sign in to create a topic at this location.
                  </p>
                )}
              </div>
            </Popup>
          </Marker>
        )}

        {/* Comment location marker - show only during comment creation and only for point drawings */}
        {isCreatingComment && commentLocation && (!currentDrawing || currentDrawing.type === 'POINT') && (() => {
          // Create a custom red icon to make the marker more visible
          const redIcon = L.icon({
            iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
            shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
            iconSize: [25, 41],
            iconAnchor: [12, 41],
            popupAnchor: [1, -34],
            shadowSize: [41, 41]
          });
          
          return (
            <Marker 
              position={[commentLocation.lat, commentLocation.lng]}
              icon={redIcon}
            >
              <Popup>
                <div style={{ textAlign: 'center' }}>
                  <h4 style={{ margin: '0 0 8px 0', color: '#dc2626' }}>💬 Comment Location</h4>
                  <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
                    {commentLocation.lat.toFixed(4)}, {commentLocation.lng.toFixed(4)}
                  </p>
                </div>
              </Popup>
            </Marker>
          );
        })()}
        
        <MapController selectedTopic={selectedTopic} searchLocation={searchLocation} currentDrawing={currentDrawing} zoomToDrawing={zoomToDrawing} focusPoint={focusPoint} focusDrawing={focusDrawing} focusBounds={focusBounds} drawings={drawings} isPanelExpanded={isPanelExpanded} selectedComment={selectedComment} commentClickCounter={commentClickCounter} comments={comments} multiSelectActive={multiSelectActive} multiSelectedCommentIds={multiSelectedCommentIds} suppressTopicFocus={suppressTopicFocus} onBoundsChange={onBoundsChange} ipRegion={ipRegion} />
        <ZoomWatcher />
        <MapViewManager />
        <MapClickHandler />
        
        {/* Drawing Tools - placeholder for future implementation */}
      </MapContainer>

      {spatialSearchPicking && (
        <div
          style={{
            position: 'absolute',
            bottom: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            background: '#111827',
            color: '#fff',
            padding: '9px 14px',
            borderRadius: 9999,
            boxShadow: '0 6px 14px rgba(0,0,0,0.2)',
            zIndex: 1200,
            fontSize: '13px',
            fontWeight: 600
          }}
        >
          Click anywhere on the map to set the search center
        </div>
      )}

      {/* Custom Zoom Controls - positioned exactly where we want them */}
      <div
        style={{
          position: 'absolute',
          top: '16px',
          left: '16px',
          zIndex: 1001,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'white',
          borderRadius: '4px',
          boxShadow: '0 1px 5px rgba(0,0,0,0.4)',
          border: '2px solid rgba(0,0,0,0.2)'
        }}
      >
        <button
          type="button"
          onClick={() => {
            try {
              if (mapInstance && typeof mapInstance.zoomIn === 'function') mapInstance.zoomIn();
            } catch (err) {
              try { console.error('Map: zoomIn failed', err); } catch (e) {}
            }
          }}
          style={{
            width: '30px',
            height: '30px',
            border: 'none',
            backgroundColor: 'white',
            borderBottom: '1px solid #ccc',
            borderRadius: '4px 4px 0 0',
            cursor: 'pointer',
            fontSize: '18px',
            fontWeight: 'bold',
            lineHeight: '1',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          title="Zoom in"
        >
          +
        </button>
        <button
          type="button"
          onClick={() => {
            try {
              if (mapInstance && typeof mapInstance.zoomOut === 'function') mapInstance.zoomOut();
            } catch (err) {
              try { console.error('Map: zoomOut failed', err); } catch (e) {}
            }
          }}
          style={{
            width: '30px',
            height: '30px',
            border: 'none',
            backgroundColor: 'white',
            borderRadius: '0 0 4px 4px',
            cursor: 'pointer',
            fontSize: '18px',
            fontWeight: 'bold',
            lineHeight: '1',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          title="Zoom out"
        >
          −
        </button>
      </div>
      {/* Floating Edit Shape Button disabled per request */}
      {false && showFloatingEditButton && (selectedTopic || selectedComment) && !editingActive && (function renderEditButton() {
        // find drawing for selection
        const selDrawing = selectedComment?.id ? drawings.find(d => (d as any).commentId === selectedComment?.id) : drawings.find(d => d.topicId === selectedTopic?.id && !d.commentId);
        const canEdit = Boolean(currentUserId && selDrawing && String(currentUserId) === String(selDrawing?.userId));
        if (!selDrawing || !canEdit) return null;
        return (
          <div style={{ position: 'absolute', top: 140, left: 12, zIndex: 1200 }}>
            <button
              type="button"
              onClick={() => onStartEditDrawing && onStartEditDrawing(selDrawing)}
              className="draw-btn"
              title="Edit shape"
              style={{ padding: '8px 10px', borderRadius: 8, background: '#f8fafc', border: '1px solid #e5e7eb' }}
            >
              ✏️ Edit shape
            </button>
          </div>
        );
      })()}


      {/* Compact color palette for drawings */}
      {(showDrawingTools || editingActive) && (
        <div
          style={{
            position: 'absolute',
            top: 46,
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: 9999,
            padding: '6px 8px',
            display: 'flex',
            gap: 8,
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
            zIndex: 1200
          }}
          role="group"
          aria-label="Drawing color"
        >
          {['#2563eb','#059669','#ef4444','#f59e0b','#8b5cf6','#6b7280'].map((c) => (
            <button
              key={c}
              onClick={() => setDrawingColor(c)}
              title={`Use ${c}`}
              style={{
                width: 18,
                height: 18,
                borderRadius: '50%',
                border: drawingColor === c ? '2px solid #111827' : '2px solid #fff',
                boxShadow: '0 0 0 1px rgba(0,0,0,0.15)',
                background: c,
                cursor: 'pointer'
              }}
            />
          ))}
        </div>
      )}


      {/* Editing controls are provided in the side panel */}

      {/* Location Search */}
      <LocationSearch onLocationFound={handleLocationFound} onExpandedChange={setSearchExpanded} />

      {/* Find Me button (below magnifier) */}
      <button 
        className="find-me-btn" 
        onClick={handleFindMe} 
        disabled={isLocating}
        title={isLocating ? "Finding your location..." : "Find my location"}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <LocateIcon size={14} /> <span className="find-me-text">{isLocating ? 'Locating…' : 'Find me'}</span>
        </span>
      </button>

      {/* Map Save Button (only shown during editing and when geocoder is not active) - positioned above shape selection */}
      {editingActive && !searchExpanded && (
        <button
          type="button"
          className="map-save-button"
          onClick={() => {
            // Trigger save in TopicPanel via unified save system
            window.dispatchEvent(new CustomEvent('map-save-edit'));
          }}
          title="Save all changes"
        >
          <DiskIcon size={16} />
        </button>
      )}
      
      {/* Always-expanded Drawing Tools (shown during editing and when geocoder is not active) */}
      {editingActive && !searchExpanded && (
        <div className="map-shape-buttons">
          <button
            type="button"
            onClick={() => applyDrawingMode('marker')}
            style={{
              padding: '8px 12px',
              fontSize: 13,
              backgroundColor: drawingMode === 'marker' ? '#3b82f6' : 'white',
              color: drawingMode === 'marker' ? 'white' : '#374151',
              border: '2px solid #e5e7eb',
              borderRadius: 6,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              fontWeight: '500'
            }}
            title="Point drawing tool"
          >
<PointIcon size={14} /> Point
          </button>
          <button
            type="button"
            onClick={() => applyDrawingMode('line')}
            style={{
              padding: '8px 12px',
              fontSize: 13,
              backgroundColor: drawingMode === 'line' ? '#2563eb' : 'white',
              color: drawingMode === 'line' ? 'white' : '#374151',
              border: '2px solid #e5e7eb',
              borderRadius: 6,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              fontWeight: '500'
            }}
            title="Line drawing tool"
          >
<LineIcon size={14} /> Line
          </button>
          <button
            type="button"
            onClick={() => applyDrawingMode('polygon')}
            style={{
              padding: '8px 12px',
              fontSize: 13,
              backgroundColor: drawingMode === 'polygon' ? '#059669' : 'white',
              color: drawingMode === 'polygon' ? 'white' : '#374151',
              border: '2px solid #e5e7eb',
              borderRadius: 6,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              fontWeight: '500'
            }}
            title="Area drawing tool"
          >
<PolygonIcon size={14} /> Area
          </button>
          {/* Persistent controls under tools during editing */}
          <div style={{ marginTop: 6, display: 'flex', gap: 8, flexDirection: 'column' }}>
            <button
              type="button"
              className="draw-btn"
              title="Clear current shape"
              onClick={() => {
                setPathPoints([]);
                setEditingVertices([]);
                setCurrentDrawing(null);
                // Notify parent that drawing is cleared
                applyDrawingMode('none');
                onClearDrawing?.();
                // Do not warn on clear; saving flow will validate and warn if user actually tries to save.
              }}
            >
              ✕ Clear
            </button>
            <button
              type="button"
              className="draw-btn"
              title="Undo last point"
              onClick={() => setPathPoints((prev) => prev.slice(0, -1))}
              disabled={pathPoints.length === 0}
            >
              ⤺ Undo
            </button>
            {/* No Finish in edit mode to avoid confusion; Save lives in header */}
          </div>
        </div>
      )}
      
      {/* Always-expanded Drawing Tools (shown in create mode) */}
      {showDrawingTools && !editingActive && drawingControlsVisible && !searchExpanded && (
        <div className="map-shape-buttons">
          <button
            type="button"
            onClick={() => {
              applyDrawingMode('marker');
              // During creation mode, don't clear currentDrawing to preserve completed shapes
              if (!onDrawingCreate) {
                setCurrentDrawing(null);
              }
            }}
            style={{
              padding: '8px 12px',
              fontSize: 13,
              backgroundColor: drawingMode === 'marker' ? '#3b82f6' : 'white',
              color: drawingMode === 'marker' ? 'white' : '#374151',
              border: '2px solid #e5e7eb',
              borderRadius: 6,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              fontWeight: '500'
            }}
            title="Point drawing tool"
          >
<PointIcon size={14} /> Point
          </button>
          <button
            type="button"
            onClick={() => {
              applyDrawingMode('line');
              // During creation mode, don't clear currentDrawing to preserve completed shapes
              if (!onDrawingCreate) {
                setCurrentDrawing(null);
              }
            }}
            style={{
              padding: '8px 12px',
              fontSize: 13,
              backgroundColor: drawingMode === 'line' ? '#2563eb' : 'white',
              color: drawingMode === 'line' ? 'white' : '#374151',
              border: '2px solid #e5e7eb',
              borderRadius: 6,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              fontWeight: '500'
            }}
            title="Line drawing tool"
          >
<LineIcon size={14} /> Line
          </button>
          <button
            type="button"
            onClick={() => {
              applyDrawingMode('polygon');
              // During creation mode, don't clear currentDrawing to preserve completed shapes
              if (!onDrawingCreate) {
                setCurrentDrawing(null);
              }
            }}
            style={{
              padding: '8px 12px',
              fontSize: 13,
              backgroundColor: drawingMode === 'polygon' ? '#059669' : 'white',
              color: drawingMode === 'polygon' ? 'white' : '#374151',
              border: '2px solid #e5e7eb',
              borderRadius: 6,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              fontWeight: '500'
            }}
            title="Area drawing tool"
          >
<PolygonIcon size={14} /> Area
          </button>
          {/* Persistent controls under tools during creation */}
          <div style={{ marginTop: 6, display: 'flex', gap: 8, flexDirection: 'column' }}>
            <button
              type="button"
              className="draw-btn"
              title="Clear current shape"
              onClick={() => {
                setPathPoints([]);
                setEditingVertices([]);
                setCurrentDrawing(null);
                setIsDrawingFinished(false);
                // Notify parent that drawing is cleared
                applyDrawingMode('none');
                onClearDrawing?.();
              }}
            >
              ✕ Clear
            </button>
            <button
              type="button"
              className="draw-btn"
              title="Undo last point"
              onClick={() => setPathPoints((prev) => prev.slice(0, -1))}
              disabled={pathPoints.length === 0}
            >
              ⤺ Undo
            </button>
            {!isDrawingFinished && (
              <button
                type="button"
                className="draw-btn"
                title="Finish shape"
                onClick={() => {
                  if (drawingMode === 'polygon' && pathPoints.length >= 3) {
                    const val = validatePolygon(pathPoints);
                    if (!val.ok) { notify({ type: 'warning', message: val.error || 'Invalid polygon' }); return; }
                    const geoJson = { type: 'Polygon', coordinates: [pathPoints.map(p => [p.lng, p.lat])] } as any;
                    const drawing = saveDrawingAsTemporary(geoJson, 'POLYGON');
                    const c = pathPoints.reduce((acc, p) => ({ lat: acc.lat + p.lat, lng: acc.lng + p.lng }), { lat: 0, lng: 0 });
                    const centroid = { lat: c.lat / pathPoints.length, lng: c.lng / pathPoints.length };
                    onDrawingCreate && onDrawingCreate({ ...drawing, topicLocation: centroid });
                    setDrawingMode('none'); setPathPoints([]); setIsDrawingFinished(true);
                  } else if (drawingMode === 'line' && pathPoints.length >= 2) {
                    const val = validateLine(pathPoints);
                    if (!val.ok) { notify({ type: 'warning', message: val.error || 'Invalid line' }); return; }
                    const geoJson = { type: 'LineString', coordinates: pathPoints.map(p => [p.lng, p.lat]) } as any;
                    const drawing = saveDrawingAsTemporary(geoJson, 'LINE');
                    const c = pathPoints.reduce((acc, p) => ({ lat: acc.lat + p.lat, lng: acc.lng + p.lng }), { lat: 0, lng: 0 });
                    const centroid = { lat: c.lat / pathPoints.length, lng: c.lng / pathPoints.length };
                    onDrawingCreate && onDrawingCreate({ ...drawing, topicLocation: centroid });
                    setDrawingMode('none'); setPathPoints([]); setIsDrawingFinished(true);
                  }
                }}
                disabled={(drawingMode === 'polygon' && pathPoints.length < 3) || (drawingMode === 'line' && pathPoints.length < 2)}
              >
                ✓ Finish
              </button>
            )}
          </div>
        </div>
      )}
      
      
      {false && (
        <div className="drawing-tools">
          <div>
            {editingActive ? (
              <span style={{ color: "#2563eb" }}>🎨 Editing shape - modify and save when ready</span>
            ) : currentDrawing ? (
              <span style={{ color: "#059669" }}>✅ Drawing created! Choose different type to replace.</span>
            ) : (
              <span>Choose ONE drawing type:</span>
            )}
          </div>
        </div>
      )}

      {/* Basemap selector */}
      <div 
        className={`basemap-selector ${basemapExpanded ? "expanded" : "collapsed"}`}
        onClick={(e) => { e.stopPropagation(); setBasemapExpanded(true); }}
      >
        {!basemapExpanded && (
          <div className="basemap-selector-icon">
            <LayersIcon size={18} />
          </div>
        )}
        {basemapExpanded && (
          <div className="basemap-selector-content" onClick={(e) => e.stopPropagation()}>
            <div className="basemap-menu">
              {BASEMAP_OPTIONS.map(({ id, name }) => (
                <button
                  key={id}
                  className={`basemap-option ${basemap === id ? "active" : ""}`}
                  onClick={() => handleSelectBasemap(id)}
                  title={`Switch to ${name}`}
                >
                  {name}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      {/* Close basemap on outside click */}
      {basemapExpanded && (
        <div
          onClick={() => setBasemapExpanded(false)}
          style={{ position: "absolute", inset: 0, zIndex: 900, background: "transparent" }}
        />
      )}

      {/* Hexagon layer loading indicator */}
      {isHexLoading && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'rgba(255, 255, 255, 0.95)',
            borderRadius: '8px',
            padding: '16px 24px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            pointerEvents: 'none'
          }}
        >
          <div
            style={{
              width: '20px',
              height: '20px',
              border: '3px solid #e5e7eb',
              borderTop: '3px solid #3b82f6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}
          />
          <span style={{ fontSize: '14px', color: '#1f2937', fontWeight: 500 }}>
            Loading map data...
          </span>
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

const MapComponent = forwardRef(MapComponentInner);

export default MapComponent;
